<template>
  <wizard-shell
    v-model="modal.active"
    @close="closeHandler"
    icon="mdi-chart-box-plus-outline"
    :title="isEditing ? 'Edit Widget' : 'Create Widget'"
    subtitle="Build a dashboard widget"
    :max-width="wizardMaxWidth"
    :views="contentViews"
    :active-view="contentView"
    @update:activeView="contentView = $event"
    :steps="wizardSteps"
    :current-step="step + 1">
        <v-alert
          v-if="saveErrors.length"
          type="error"
          dense
          outlined
          dismissible
          class="mb-3"
          role="alert"
          @input="saveErrors = []">
          <div class="font-weight-medium">The widget was not saved.</div>
          <ul class="mb-0">
            <li v-for="(message, index) in saveErrors" :key="index">{{ message }}</li>
          </ul>
        </v-alert>
        <v-row no-gutters style="height: 100%">

          <!-- ════════════ STEP 1: Select View & Columns ════════════ -->
          <template v-if="step === 0">
            <!-- Config view -->
            <div v-show="contentView === 'config'" class="sql-view-config-pane sql-view-config-pane--fill">
              <!-- View selector -->
              <label for="widget-data-source" class="ds-field-label">
                Data Source
                <span class="ds-field-label__tag ds-field-label__tag--required">Required</span>
              </label>
              <v-autocomplete
                id="widget-data-source"
                v-model="config.tableName"
                :items="groupedViews"
                :item-text="item => item.displayName || item.name"
                item-value="name"
                placeholder="Search data sources..."
                outlined
                dense
                hide-details
                :loading="viewsLoading"
                :item-disabled="isSourceNotConfigured"
                @change="onViewSelected"
                class="mb-3 flex-grow-0">
                <!-- AVA-602: a Facility Group register with rows but no Shared/Separated mode
                     cannot be read, so it is listed but disabled. Vuetify disabled rows are
                     pointer-events:none (no hover tooltip), so the reason is a visible line. -->
                <template #item="{ item }">
                  <div class="dsrc-item">
                    <v-icon small :color="isSourceNotConfigured(item) ? undefined : 'primary'">mdi-table</v-icon>
                    <div class="dsrc-item__text">
                      <span>{{ item.displayName || item.name }}</span>
                      <span v-if="isSourceNotConfigured(item)" class="dsrc-item__hint">
                        {{ notConfiguredCopy.pickerHint }}
                      </span>
                    </div>
                    <v-chip v-if="isSourceNotConfigured(item)" x-small label outlined class="dsrc-item__chip">
                      {{ notConfiguredCopy.chip }}
                    </v-chip>
                  </div>
                </template>
              </v-autocomplete>

              <!-- Column picker -->
              <div v-if="availableColumns.length" class="flex-grow-1 d-flex flex-column" style="overflow: hidden; min-height: 220px;">
                <span class="ds-field-label">
                  Columns
                  <span class="ds-field-label__tag ds-field-label__tag--required">Required</span>
                </span>
                <widget-column-chooser
                  :columns="availableColumns"
                  :samples="columnSamples"
                  :value="config.columns"
                  @input="onColumnsChanged">
                </widget-column-chooser>
              </div>

              <div v-else-if="config.tableName && !columnsLoading && columnsConfigRequired" class="dsrc-config-required pa-3" role="status">
                <span class="dsrc-config-required__title">{{ notConfiguredCopy.title }}</span>
                <span class="dsrc-config-required__text">{{ notConfiguredCopy.message }}</span>
              </div>

              <div v-else-if="config.tableName && !columnsLoading" class="text-caption text--disabled pa-3">
                No columns found for this view.
              </div>

              <div v-else-if="columnsLoading" class="d-flex justify-center align-center flex-grow-1">
                <envizion-loader :size="48" :color="$vuetify.theme.dark ? '#2cc295' : '#03624c'" label="Loading columns" />
              </div>

              <!-- Filters: which rows the widget consumes; part of the dataset definition,
                   and the raw-rows preview on this step makes their effect immediately visible -->
              <section v-if="availableColumns.length" class="viz-section mt-4">
                <div class="viz-section__head">
                  <span class="ds-field-label">
                    Filters
                    <span class="ds-field-label__tag ds-field-label__tag--optional">Optional</span>
                  </span>
                  <v-btn
                    small
                    outlined
                    color="primary"
                    class="viz-section__action"
                    @click="$refs.filterBuilder.addFilter()">
                    <v-icon small class="mr-1">mdi-plus</v-icon> Add filter
                  </v-btn>
                </div>
                <sql-view-filter-builder
                  ref="filterBuilder"
                  v-model="config.filters"
                  :columns="selectedColumnsWithTypes">
                </sql-view-filter-builder>
              </section>
            </div>

            <!-- Preview view -->
            <div v-show="contentView === 'preview'" style="width: 100%; height: 100%;">
              <sql-view-widget-preview
                ref="preview"
                :config="previewConfig"
                :library-schemas="librarySchemas"
                :message-type="'table'"
                :title="config.title || config.tableDisplayName || config.tableName || 'Preview'"
                :sub-title="config.subTitle"
                @data-loaded="onPreviewData">
              </sql-view-widget-preview>
            </div>
          </template>

          <!-- ════════════ STEP 2: Build visualization ════════════ -->
          <template v-if="step === 1">
            <!-- Config view -->
            <div v-show="contentView === 'config'" class="sql-view-config-pane sql-view-config-pane--fill sql-view-config-pane--gap-lg">
              <!-- Widget Details: the widget's basic identity (title, subtitle), placed first in the
                   Build step above the display-type picker at the user's request, as part of the
                   widget's basic definition. Auto refresh lives in Behavior & finish. -->
              <section class="viz-section">
                <div class="viz-section__head">
                  <span class="ds-field-label">
                    Widget Details
                    <span class="ds-field-label__tag ds-field-label__tag--optional">Optional</span>
                  </span>
                </div>
                <span class="viz-sub-label">Title</span>
                <v-text-field
                  v-model="config.title"
                  :placeholder="config.tableDisplayName || config.tableName || 'Widget title'"
                  outlined
                  dense
                  hide-details>
                </v-text-field>
                <div class="mt-3">
                  <span class="viz-sub-label">Subtitle</span>
                  <v-text-field
                    v-model="config.subTitle"
                    placeholder="Optional description"
                    outlined
                    dense
                    hide-details>
                  </v-text-field>
                </div>
              </section>

              <!-- Visualization type: family-grouped tile picker -->
              <div class="viz-section">
                <span class="ds-field-label">
                  Display Type
                  <span class="ds-field-label__tag ds-field-label__tag--required">Required</span>
                </span>
                <widget-display-type-picker v-model="displayTypeKey"></widget-display-type-picker>

                <!-- Destructive type-switch notice: shown only when a switch actually cleared
                     incompatible configuration (an inapplicable colour schema, or the KPI status
                     settings when switching to a gauge). The lighter inline pattern, not a blocking
                     dialog; auto-clears on the next switch and on reopen. -->
                <div v-if="typeSwitchNotice" class="viz-type-notice" role="status">
                  <v-icon small color="primary">mdi-information-outline</v-icon>
                  <span class="viz-type-notice__text">{{ typeSwitchNotice }}</span>
                  <v-btn x-small text class="viz-type-notice__dismiss" @click="typeSwitchNotice = ''">Dismiss</v-btn>
                </div>
              </div>

              <!-- Group & summarize (always visible; chart/KPI types need a calculation) -->
              <section class="viz-section">
                <div class="viz-section__head">
                  <span class="ds-field-label">
                    {{ config.messageType === 'count' ? 'What to count' : 'Group Results' }}
                    <span
                      class="ds-field-label__tag"
                      :class="requiresMetric ? 'ds-field-label__tag--required' : 'ds-field-label__tag--optional'">
                      {{ requiresMetric ? 'Required' : 'Optional' }}
                    </span>
                  </span>
                  <v-btn
                    v-if="canChangeMetricCount"
                    small
                    outlined
                    color="primary"
                    class="viz-section__action"
                    :disabled="config.messageType !== 'count' && !hasAggregation"
                    @click="$refs.aggBuilder.addMetric()">
                    <v-icon small class="mr-1">mdi-plus</v-icon> {{ config.messageType === 'count' ? 'Choose what to count' : 'Add calculation' }}
                  </v-btn>
                </div>
                <sql-view-aggregation-builder
                  ref="aggBuilder"
                  v-model="config.aggregation"
                  :columns="selectedColumnsWithTypes"
                  :require-group-by="config.messageType !== 'count'"
                  :group-by-hint="groupByHint">
                </sql-view-aggregation-builder>
                <div v-if="requiresMetric && !hasMetric" class="viz-metric-hint" role="status">
                  <v-icon small color="primary">mdi-information-outline</v-icon>
                  <span>{{ metricHintText }}</span>
                </div>
                <div v-if="config.messageType === 'count' && hasMetric" class="viz-metric-hint" role="status">
                  <v-icon small color="primary">mdi-information-outline</v-icon>
                  <span>A KPI shows a single total, so there is no grouping to set.</span>
                </div>
              </section>
            </div>

            <!-- Preview view -->
            <div v-show="contentView === 'preview'" style="width: 100%; height: 100%;">
              <sql-view-widget-preview
                ref="preview"
                :config="previewConfig"
                :library-schemas="librarySchemas"
                :message-type="config.messageType"
                :title="config.title || config.tableDisplayName || config.tableName || 'Preview'"
                :sub-title="config.subTitle"
                :font-size="previewFontSize"
                :widget-size="config.widgetSize"
                :legend="config.legend">
              </sql-view-widget-preview>
            </div>
          </template>

          <!-- ════════════ STEP 3: Behavior & finish ════════════ -->
          <template v-if="step === 2">
            <!-- Config view: type-specific appearance, 3D linking/coloring, refresh + identity -->
            <div
              v-show="contentView === 'config'"
              class="sql-view-behavior-pane">
              <wizard-step-behavior
              :config="config"
              :status-modes="STATUS_MODE"
              :kpi-directions="KPI_DIRECTION"
              :chart-coloring-modes="CHART_COLORING_MODE"
              :is-stacked-type="isStackedType"
              :is-dual-axis-type="isDualAxisType"
              :is-gauge-type="isGaugeType"
              :is-chart-type="isChartType"
              :chart-supports-color-schema="chartSupportsColorSchema"
              :dual-axis-metrics="dualAxisMetrics"
              :line-type-options="lineTypeOptions"
              :kpi-icon-options="kpiIconOptions"
              :kpi-tone-options="kpiToneOptions"
              :kpi-has-threshold="kpiHasThreshold"
              :kpi-caution-hint="kpiCautionHint"
              :kpi-critical-hint="kpiCriticalHint"
              :kpi-threshold-error="kpiThresholdError"
              :gauge-config-error="gaugeConfigError"
              :kpi-schema-select-items="kpiSchemaSelectItems"
              :kpi-schema-library-loading="kpiSchemaLibraryLoading"
              :kpi-schema-show-details="kpiSchemaShowDetails"
              :kpi-schema-show-band-badge="kpiSchemaShowBandBadge"
              :kpi-schema-unit-items="kpiSchemaUnitItems"
              :kpi-schema-band-preview="kpiSchemaBandPreview"
              :kpi-schema-is-custom="kpiSchemaIsCustom"
              :kpi-schema-missing-reference="kpiSchemaMissingReference"
              :legend-positions="legendPositions"
              :datagrid-sort-columns="datagridSortColumns"
              :datagrid-numeric-columns="datagridNumericColumns"
              :colouring-column-items="colouringColumnItems"
              :coloring-values="coloringValues"
              :coloring-values-loading="coloringValuesLoading"
              :spatial-capability="selectedSpatialCapability"
              :poi-applicable="poiApplicable"
              :poi-unavailable-note="poiUnavailableNote"
              :poi-highlight-items="poiHighlightItems"
              :poi-context-items="poiContextItems"
              :refresh-intervals="refreshIntervals"
              @link3d-change="onLink3DChange"
              @coloring-column-change="onColoringColumnChange"
              @schema-change="onSchemaSelected">
              </wizard-step-behavior>

              <section v-if="isComparisonType" class="viz-section">
                <div class="viz-section__head">
                  <span class="ds-field-label">
                    Comparison line
                    <span class="ds-field-label__tag ds-field-label__tag--optional">Optional</span>
                  </span>
                </div>
                <span class="viz-sub-label">Reference calculation</span>
                <v-select
                  v-model="config.comparison.referenceMetricAlias"
                  :items="comparisonMetricItems"
                  item-text="text"
                  item-value="value"
                  placeholder="No reference line"
                  clearable
                  outlined
                  dense
                  hide-details
                  class="viz-narrow-select">
                </v-select>
                <span class="viz-sub-hint">The selected calculation is drawn as a dashed red line; the others stay grouped columns.</span>
              </section>
            </div>

            <!-- Preview view -->
            <div v-show="contentView === 'preview'" style="width: 100%; height: 100%;">
              <sql-view-widget-preview
                ref="preview"
                :config="previewConfig"
                :library-schemas="librarySchemas"
                :message-type="config.messageType"
                :title="config.title || config.tableDisplayName || config.tableName || 'Preview'"
                :sub-title="config.subTitle"
                :font-size="previewFontSize"
                :widget-size="config.widgetSize"
                :legend="config.legend">
              </sql-view-widget-preview>
            </div>
          </template>

        </v-row>
      <template #footer>
        <v-btn v-if="step > 0" outlined @click="step--">
          <v-icon left>mdi-chevron-left</v-icon> Back
        </v-btn>
        <v-btn outlined color="error" @click="closeHandler">Cancel</v-btn>
        <v-spacer></v-spacer>
        <span
          v-if="blockReason"
          class="text-caption warning--text mr-3 d-flex align-center"
          style="white-space: nowrap;">
          <v-icon x-small color="warning" class="mr-1">mdi-alert-circle-outline</v-icon>
          {{ blockReason }}
        </span>
        <v-btn
          v-if="step < 2"
          color="primary"
          depressed
          :disabled="!canAdvance"
          @click="step++">
          Continue <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          color="primary"
          depressed
          :loading="modal.loading"
          :disabled="!canConfirm"
          @click="confirmHandler">
          {{ isEditing ? 'Update Widget' : 'Create Widget' }}
          <v-icon right>mdi-check</v-icon>
        </v-btn>
      </template>
  </wizard-shell>
</template>

<script>
/* eslint-disable */
import SqlViewWidgetPreview from './SqlViewWidgetPreview.vue';
import SqlViewFilterBuilder from './SqlViewFilterBuilder.vue';
import SqlViewAggregationBuilder from './SqlViewAggregationBuilder.vue';
import WidgetColumnChooser from './WidgetColumnChooser.vue';
import WidgetDisplayTypePicker from './WidgetDisplayTypePicker.vue';
import WizardShell from '@/features/Share/components/wizard/WizardShell.vue';
import WizardStepBehavior from './WizardStepBehavior.vue';
import apiService from '../service/dashboard-widgets.service';
import { log, error } from '@/utils/debugLogger';
import {
  COLORING_SOURCE,
  SCHEMA_KIND,
  APPLIES_TO,
  TONE,
  UNIT,
  BUILT_IN_SCHEMAS,
  getBuiltInSchema,
  canConvert,
} from '@/features/Dashboard/utils/coloringSchemas';
import { STACKED_COLUMN, DUAL_AXIS_LINE, HEATMAP, MIXED_COMPARISON, MATRIX_TYPES } from '@/features/Dashboard/charts/echartsOptions';
import { LEGACY_FONT_SIZE_BY_WIDGET_SIZE, resolveWidgetSize } from '@/features/Dashboard/utils/widgetSize';
import { buildSourceKey } from '@/features/Dashboard/utils/sourceKey';
import { metricLabel } from '@/features/Dashboard/utils/metricLabel';
import { normalizeColumnColoring } from '@/features/Dashboard/utils/columnColoring';
import { resolveTagLink } from '@/features/Dashboard/utils/tagLink';
import {
  WIDGET_CONFIG_SCHEMA_VERSION,
  preservedConfigEntries,
  preservedEntriesForSource,
  withPreservedEntries,
  widgetSaveErrorMessages,
  aggregationForType,
  sortForColumns,
  filtersToStore,
} from '@/features/Dashboard/utils/widgetConfigContract';
import { betweenFilterError } from '@/features/Dashboard/utils/betweenFilter';
import {
  REGISTER_NOT_CONFIGURED_COPY,
  isConfigurationRequiredSource,
  isRegisterConfigurationRequiredError,
} from '@/utils/registerConfigurationError';
import {
  hasSpatialBindingCapability,
  spatialFocusOverrideForStorage,
} from '@/features/Dashboard/utils/spatialFocusPolicy';
import {
  normalizePoiSource,
  POI_SOURCE_INHERIT,
  POI_SOURCE_NONE,
  POI_HIGHLIGHT_TYPES,
  POI_CONTEXT_TYPES,
  POI_HIGHLIGHT_DISABLED_REASON,
  isPoiLinkColumn,
  computeEffectivePoi,
} from '@/features/Dashboard/utils/poiConfig';
import registerService from '@/features/Registers/services/registerService';
import {
  POI_SCHEMA_DENIED_NOTE,
  POI_SCHEMA_STATUS,
  poiSchemaStatusFromError,
  resolvePoiForcedColumns,
} from '@/features/Dashboard/utils/poiSchemaAccess';

// Default swatch palette cycled through when auto-seeding a colour per unique value.
// Distinct, high-contrast hues so an unconfigured map is still legible in 3D.
const COLORING_PALETTE = [
  '#1E88E5', '#43A047', '#FB8C00', '#E53935', '#8E24AA',
  '#00ACC1', '#FDD835', '#6D4C41', '#3949AB', '#7CB342',
  '#D81B60', '#00897B', '#F4511E', '#5E35B1', '#C0CA33',
];

// Curated MDI icons offered for the Number/KPI card, chosen for industrial
// inspection/integrity relevance. '' (None, the default) lives in the picker template,
// not this list. Persisted as sqlViewConfig.icon and rendered on the builder-v1 A+ card.
const KPI_ICON_OPTIONS = Object.freeze([
  'mdi-alert-circle-outline',
  'mdi-gauge',
  'mdi-wrench-outline',
  'mdi-shield-check-outline',
  'mdi-water-outline',
  'mdi-fire',
  'mdi-chart-line-variant',
  'mdi-clipboard-check-outline',
  'mdi-thermometer',
  'mdi-counter',
]);

// Severity tone choices for the Number/KPI card (count widgets only). '' (None, the default)
// lives in the picker template. Swatch colours mirror the value colours rendered on the A+ card
// (castleton / text-safe gold / error). Persisted as sqlViewConfig.tone.
const KPI_TONE_OPTIONS = Object.freeze([
  { value: TONE.GOOD, label: 'Good', color: '#03624c' },
  { value: TONE.CAUTION, label: 'Caution', color: '#a07a00' },
  { value: TONE.CRITICAL, label: 'Critical', color: '#b71c1c' },
]);

// Line-type choices for a dual-axis line metric. 'solid' is the default (ECharts' own default
// lineStyle.type), so an untouched metric renders byte-identically. Maps 1:1 to ECharts
// series-line.lineStyle.type. Persisted per metric in sqlViewConfig.seriesStyles.
const LINE_TYPE_OPTIONS = Object.freeze([
  { value: 'solid', text: 'Solid' },
  { value: 'dashed', text: 'Dashed' },
  { value: 'dotted', text: 'Dotted' },
]);

// Frozen KPI/chart status vocabulary (the wizard's new coloring fields). Named enums instead of
// scattered string literals so the picker toggle values, the v-if branches, and the serializers
// stay in lockstep. Exposed on the instance (data) so template bindings can use them too.
// KPI dynamic-status direction - which way the number turns bad (derives the threshold op).
const KPI_DIRECTION = Object.freeze({
  HIGHER_IS_WORSE: 'higherIsWorse',
  LOWER_IS_WORSE: 'lowerIsWorse',
});
// Count/KPI status mode: dynamic thresholds, a fixed manual tone, or a range schema.
const STATUS_MODE = Object.freeze({
  AUTO: 'auto',
  FIXED: 'fixed',
  SCHEMA: 'schema',
});
// Chart series-coloring mode: today's categorical palette, or a range schema.
const CHART_COLORING_MODE = Object.freeze({
  STATIC: 'static',
  SCHEMA: 'schema',
});

// Aggregated-row cap the matrix bake requests (raises the server default 100 -> 1000) AND the same
// number prepareMatrixData (chart-data.service.js) tests its truncation sentinel against. This owns
// the wizard/payload side; the service keeps its own paired copy - keep the two in sync.
const MATRIX_ROW_CAP = 1000;

// Sentinel dropdown value for a LEGACY inline schema (created before the registry). Distinct from
// every built-in SCHEMA_ID and every library schema uuid. The wizard is select-only now, so inline
// schemas are no longer creatable; an existing inline widget restores to this and re-serializes its
// original selection verbatim (never rebuilt — a rebuild would drift the unit/id).
const CUSTOM_SCHEMA_ID = '__custom__';

// Group headers for the data-source picker. The list arrives as three families in one flat array
// (SqlViewQueryService.GetAvailableViewsAsync): the built-in vw_dashboard_* views, which carry
// neither id; the register views, which carry registerId; and the compiled composite sources,
// which carry sourceId. All three are headed - a headed list with an unlabeled block floating
// above it reads as an oversight rather than as a group.
const VIEW_GROUP_HEADER = Object.freeze({
  DashboardViews: 'Dashboard views',
  Registers: 'Register sources',
  CompositeSources: 'Composite sources',
});

const COMPILER_HIDDEN_COLUMNS = new Set([
  '__rowkey',
  '__registerrowid',
  '__targetid',
  '__contexttagid',
]);

function isHiddenColumnMetadata(column) {
  return !!column && (column.hidden === true
    || COMPILER_HIDDEN_COLUMNS.has(String(column.name || '').toLowerCase()));
}

function sanitizeAggregationColumns(aggregation, isHidden) {
  if (!aggregation || typeof aggregation !== 'object') return aggregation;
  const sanitized = {
    ...aggregation,
    groupBy: (aggregation.groupBy || []).filter(column => !isHidden(column)),
    metrics: (aggregation.metrics || []).filter(metric => metric && !isHidden(metric.column)),
  };
  const bucket = aggregation.differenceBucketGroup;
  if (bucket && (isHidden(bucket.leftColumn) || isHidden(bucket.rightColumn))) {
    sanitized.differenceBucketGroup = null;
  }
  return sanitized;
}

// Slugify an aggregation function + column into a stable metric alias key ({fn}_{column},
// lowercased, non-alphanumeric → underscore). Matrix chart types persist a GENERATED, unique
// alias per metric (Codex R1-2) so the raw dict rows carry a predictable, unambiguous value key
// the normalizer (prepareMatrixData) can read strictly instead of guessing.
function slugifyAlias(fn, column) {
  const raw = `${fn || 'value'}_${column || 'col'}`;
  return raw.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'value';
}

export default {
  components: {
    SqlViewWidgetPreview,
    SqlViewFilterBuilder,
    SqlViewAggregationBuilder,
    WidgetColumnChooser,
    WidgetDisplayTypePicker,
    WizardShell,
    WizardStepBehavior,
  },

  props: {
    // The current tab's coloring schemas, passed down by CustomDashboard (the registry owner).
    // null = still loading (or none yet) — the dropdown shows a loading state and a restored
    // reference renders neutral rather than "missing"; an array (even empty) = loaded.
    librarySchemas: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      modal: { active: false, loading: false },
      // sqlViewConfig entries this wizard does not own (e.g. written by the AVA MCP), captured from
      // the config the wizard was opened with and carried over on save so an edit never drops them.
      preservedConfig: {},
      // The table the widget was opened on: a carried-over listDrill only fits that source.
      preservedConfigTable: null,
      // Messages from a rejected save (400 WIDGET_CONFIG_INVALID). The wizard stays open to show them.
      saveErrors: [],
      // Stash of a restored LEGACY inline schemaSelection (kind:'inline'), so an existing inline
      // widget re-serializes byte-identically instead of being rebuilt (which would drift). Set on
      // open() when the saved selection is inline; cleared on every open() so it can't leak.
      legacyInlineSelection: null,

      // Wizard rail steps (WizardShell). Split into three: Data (source/columns/filters),
      // Build visualization (identity + display type + metric/grouping), and Behavior & finish
      // (type-specific appearance, 3D, refresh) so no single step is overloaded.
      wizardSteps: [
        { title: 'Data', description: 'Source, columns & filters', heading: 'Select your data', subheading: 'Choose a data source, the columns to include, and any filters.' },
        { title: 'Build visualization', description: 'Display type, metric & grouping', heading: 'Build the visualization', subheading: 'Pick a display type, then choose the metric and grouping it shows.' },
        { title: 'Behavior & finish', description: 'Appearance, 3D & details', heading: 'Behavior & finish', subheading: 'Set the type-specific appearance, 3D behavior, and refresh.' },
      ],
      step: 0,
      // Rail Config/Preview toggle — which pane the content area shows (reset per step)
      contentView: 'config',
      // Inline notice on the Build-visualization step when a display-type switch cleared
      // incompatible configuration (an inapplicable colour schema, or the KPI status when
      // switching to a gauge). '' = hidden; cleared on the next switch and on every open().
      typeSwitchNotice: '',
      isEditing: false,
      editingWidgetId: null,

      // Available views and columns from API
      availableViews: [],
      viewsLoading: false,
      availableColumns: [],
      hiddenColumnNames: [],
      columnsLoading: false,
      // AVA-602: the selected source's columns were refused because the register needs a mode
      // (reachable when editing a saved widget; new selection of such a source is disabled).
      columnsConfigRequired: false,
      notConfiguredCopy: REGISTER_NOT_CONFIGURED_COPY,
      // name → sample value for the column chooser, harvested from the preview fetch
      // (accumulated per data source; cleared when the source changes)
      columnSamples: {},

      // 3D colour-coding (3a): unique values of the chosen colouring column + fetch state
      coloringValues: [],
      coloringValuesLoading: false,

      // Register POI (Step 4): the current view's register link declarations (client shape), fetched
      // once the view resolves to a register id. Empty for a non-register view — the picker hides.
      poiDeclarations: [],
      // State of that schema read (POI_SCHEMA_STATUS). DENIED (403) hides the picker and says why;
      // anything but OK means the declarations are unknown, so a save keeps the saved POI columns.
      poiSchemaStatus: POI_SCHEMA_STATUS.IDLE,
      // The columns the edited widget was last saved with (null in create mode). A save that could
      // not read the schema keeps the register-default POI columns from here (resolvePoiForcedColumns).
      poiSavedColumns: null,

      // Widget configuration
      config: this.defaultConfig(),

      // UI constants (display types live in WidgetDisplayTypePicker)
      legendPositions: [
        { name: 'Top', value: 'top' },
        { name: 'Bottom', value: 'bottom' },
        { name: 'Left', value: 'left' },
        { name: 'Right', value: 'right' },
      ],
      refreshIntervals: [
        { text: 'Off (Manual)', value: null },
        { text: 'Every 5 minutes', value: 5 },
        { text: 'Every 15 minutes', value: 15 },
        { text: 'Every 30 minutes', value: 30 },
        { text: 'Every 1 hour', value: 60 },
      ],
      // Curated icon choices for the Number/KPI picker (count widgets only).
      kpiIconOptions: KPI_ICON_OPTIONS,
      // Severity tone choices for the Number/KPI card (count widgets only).
      kpiToneOptions: KPI_TONE_OPTIONS,
      // Line-type choices for the dual-axis per-metric styling row.
      lineTypeOptions: LINE_TYPE_OPTIONS,
      // Frozen status/coloring vocabulary, exposed for template bindings (toggle values + v-if).
      STATUS_MODE,
      KPI_DIRECTION,
      CHART_COLORING_MODE,
    };
  },

  computed: {
    /**
     * The active facility id (store.state.assetId - the same source the widget service's
     * facilityHeaders() reads). Wrapped in a computed per the store-access convention; used at
     * save time to stamp the widget's sourceKey scope. Null when no facility is active, which
     * makes buildSourceKey return null and the key fall back to runtime derivation on read.
     */
    facilityId() {
      return this.$store.state.assetId || null;
    },

    /** Rail view-switch options (Config / Preview) fed to WizardShell. */
    contentViews() {
      return [
        { key: 'config', label: 'Config', icon: 'mdi-tune-variant' },
        { key: 'preview', label: 'Preview', icon: 'mdi-eye-outline' },
      ];
    },

    /** Dialog widens in Preview so wide sample data breathes; Config stays compact. */
    wizardMaxWidth() {
      return this.contentView === 'preview' ? 1560 : 1080;
    },

    /**
     * The picker's items, with the composite sources gathered under their own header so a
     * compiled vw_Source_* is findable as a kind of thing rather than sorted in among the
     * registers by name.
     *
     * LOCAL TO THE PICKER ON PURPOSE. availableViews stays the raw server list because three
     * other readers index into it by name (poiRegisterId, onViewSelected, the display-name
     * lookup), and header entries have no name - injecting them into the source array would put
     * objects those lookups must then learn to skip.
     *
     * Vuetify drops header entries while the autocomplete is being typed in (VAutocomplete's
     * filteredItems reads item-text, which is undefined for a header, so the filter excludes
     * it). That is accepted: search results are a flat list and the grouping is a browsing aid.
     */
    groupedViews() {
      const views = this.availableViews || [];
      const dashboardViews = views.filter(v => !v.registerId && !v.sourceId);
      const registers = views.filter(v => v.registerId && !v.sourceId);
      const sources = views.filter(v => v.sourceId);

      // A header is only emitted for a family that has members, so an empty one never leaves a
      // heading with nothing under it.
      const items = [];
      if (dashboardViews.length) items.push({ header: VIEW_GROUP_HEADER.DashboardViews }, ...dashboardViews);
      if (registers.length) items.push({ header: VIEW_GROUP_HEADER.Registers }, ...registers);
      if (sources.length) items.push({ header: VIEW_GROUP_HEADER.CompositeSources }, ...sources);

      return items;
    },

    selectedColumnsWithTypes() {
      return this.availableColumns.filter(c => this.config.columns.includes(c.name));
    },

    /** Columns offered as the 3D colouring driver — the widget's own columns, each
     *  guaranteed a display label so the select never renders blank. The virtual register
     *  TagId is excluded: it is a GUID (meaningless to colour by) and its distinct-values
     *  lookup 500s server-side because the column is not physically on the register view. */
    colouringColumnItems() {
      return this.selectedColumnsWithTypes
        .filter(c => c.name !== 'TagId')
        .map(c => ({
          name: c.name,
          displayName: c.displayName || c.name,
        }));
    },

    selectedSpatialCapability() {
      const tableName = String(this.config.tableName || '').toLowerCase();
      const view = (this.availableViews || [])
        .find(item => String(item?.name || '').toLowerCase() === tableName);
      return view?.spatial || null;
    },

    spatialFocusStorageDecision() {
      if (!this.config.link3D) return { value: null, error: null };
      if (this.config.spatialFocus?.mode !== 'override'
        && !hasSpatialBindingCapability(this.selectedSpatialCapability)) {
          return { value: null, error: null };
        }
      return spatialFocusOverrideForStorage(
        this.selectedSpatialCapability,
        this.config.spatialFocus,
      );
    },

    /** Register id backing the current view, resolved from the dashboard view list (Step 3a put
     *  registerId on each register view). null for a non-register view → the POI picker hides. */
    poiRegisterId() {
      const tableName = this.config.tableName;
      if (!tableName) return null;
      const match = (this.availableViews || []).find(
        v => String(v?.name).toLowerCase() === String(tableName).toLowerCase(),
      );
      if (!match?.spatial?.target) return null;
      return match?.registerId || null;
    },

    /** Whether the register-POI picker applies: a register-backed GRID **or CHART** whose register
     *  declares at least one resolvable link. No declared links → a dead Off/Off picker, so show
     *  nothing. Fail-closed: not applicable → nothing writable → byte-identical, which also
     *  guarantees the seeded widgets over undesignated registers are untouched.
     *
     *  Charts were excluded originally ("charts cannot execute a POI config") because the chart path
     *  had no POI awareness. It does now — CustomDashboard.buildChartPoiPaint composes stored links
     *  over the whole-tag paint — but it read the REGISTER's declared default, so every chart on a
     *  register behaved identically and two charts on one board could not differ. Admitting charts
     *  here is what gives each chart its own highlight choice; the wizard hides context/dim for
     *  charts because that path paints additive only. */
    poiApplicable() {
      return (this.config.messageType === 'datagrid' || this.isChartType)
        && !!this.config.spatial?.target
        && !!this.poiRegisterId
        && this.poiDeclarations.some(isPoiLinkColumn);
    },

    /** Why the POI picker is missing where it would otherwise apply (same widget-type and spatial
     *  gates as poiApplicable) because this user cannot read the register schema (403). null
     *  otherwise - no note for non-register views, other widget types or other failures. */
    poiUnavailableNote() {
      const pickerWouldApply = (this.config.messageType === 'datagrid' || this.isChartType)
        && !!this.config.spatial?.target
        && !!this.poiRegisterId;
      const denied = this.poiSchemaStatus === POI_SCHEMA_STATUS.DENIED;
      return denied && pickerWouldApply ? POI_SCHEMA_DENIED_NOTE : null;
    },

    /** Highlight select options from the register declarations. Off first; "Register default" only
     *  when a highlight-capable default is declared; then each declared link column — highlight-
     *  capable enabled, the rest (context-only / reference-only) DISABLED with a reason (plan §4). */
    poiHighlightItems() {
      const decls = this.poiDeclarations || [];
      const items = [{ text: 'Off', value: POI_SOURCE_NONE }];
      const def = decls.find(
        c => c && c.isDefaultHighlight === true && POI_HIGHLIGHT_TYPES.includes(c.linkEntityType),
      );
      if (def) {
        items.push({ text: `Register default (${def.displayName || def.name})`, value: POI_SOURCE_INHERIT });
      }
      decls.forEach((c) => {
        if (!isPoiLinkColumn(c)) return;
        const label = c.displayName || c.name;
        // value = the DISPLAY name: it is the physical view column the query/grid use. Storing the
        // internal `name` (field_1) makes the forced column invalid ("Invalid column: field_1").
        const columnValue = c.displayName || c.name;
        if (POI_HIGHLIGHT_TYPES.includes(c.linkEntityType)) {
          items.push({ text: label, value: columnValue });
        } else {
          const reason = POI_HIGHLIGHT_DISABLED_REASON[c.linkEntityType];
          items.push({ text: reason ? `${label} (${reason})` : label, value: columnValue, disabled: true });
        }
      });
      return items;
    },

    /** Context select options: Off, "Register default" (when a context default is declared), then
     *  each context-capable declared column. Reference-only types (document) are simply not offered
     *  — context needs geometry to dim. */
    poiContextItems() {
      const decls = this.poiDeclarations || [];
      const items = [{ text: 'Off', value: POI_SOURCE_NONE }];
      const def = decls.find(
        c => c && c.isDefaultContext === true && POI_CONTEXT_TYPES.includes(c.linkEntityType),
      );
      if (def) {
        items.push({ text: 'Register default', value: POI_SOURCE_INHERIT });
      }
      decls.forEach((c) => {
        if (!isPoiLinkColumn(c) || !POI_CONTEXT_TYPES.includes(c.linkEntityType)) return;
        // value = display name (physical column), never the internal `name`. See highlight items.
        items.push({ text: c.displayName || c.name, value: c.displayName || c.name });
      });
      return items;
    },

    hasAggregation() {
      const aggregation = this.config.aggregation;
      return !!(aggregation && (
        (aggregation.groupBy && aggregation.groupBy.length > 0) ||
        !this.canChangeMetricCount
      ));
    },

    canChangeMetricCount() {
      return !(this.config.aggregation && this.config.aggregation.differenceBucketGroup);
    },

    hasFilters() {
      return this.config.filters && this.config.filters.length > 0;
    },

    isChartType() {
      return ['pie', 'donut', 'bar', 'column', 'area', 'line', STACKED_COLUMN, DUAL_AXIS_LINE, MIXED_COMPARISON].includes(this.config.messageType);
    },

    /** True for the multi-series matrix types (stacked column, dual-axis line) that consume a matrix
     *  normalizer and persist generated aliases + a baked TopBottom. */
    isMatrixType() {
      return MATRIX_TYPES.includes(this.config.messageType);
    },

    /** True for stacked column — carries the stack/group layout toggle. */
    isStackedType() {
      return this.config.messageType === STACKED_COLUMN;
    },

    /** True for dual-axis line — its series come from the metrics (not a second group-by), so it
     *  carries the per-metric left/right axis assignment (axisByMetric). */
    isDualAxisType() {
      return this.config.messageType === DUAL_AXIS_LINE;
    },

    isComparisonType() {
      return this.config.messageType === MIXED_COMPARISON;
    },

    /** The metrics (with generated aliases) offered for per-metric axis assignment in the wizard.
     *  Each carries a human `label` (e.g. "Count of records") so the axis/style rows show the same
     *  vocabulary as the metric list instead of the raw generated alias; `alias` stays the key. */
    dualAxisMetrics() {
      const metrics = (this.matrixAggregation && this.matrixAggregation.metrics) || [];
      const names = this.config.columnDisplayNames || {};
      return metrics.map(m => ({ ...m, label: metricLabel(m, names) }));
    },

    comparisonMetricItems() {
      if (!this.isComparisonType) return [];
      const names = this.config.columnDisplayNames || {};
      return ((this.matrixAggregation && this.matrixAggregation.metrics) || []).map(m => ({
        value: m.alias,
        text: metricLabel(m, names) || m.alias,
      }));
    },

    /** True for the gauge display variant (a count widget with kpiVariant 'gauge'). */
    isGaugeType() {
      return this.config.messageType === 'count' && this.config.kpiVariant === 'gauge';
    },

    /** The picker's display-type key. Gauge is not a real messageType (it rides the count/scalar
     *  path, Codex R1-1); the picker offers it as its own tile and this get/set maps it to
     *  messageType 'count' + kpiVariant 'gauge'. Selecting any non-gauge type clears kpiVariant; a
     *  fresh switch to gauge also resets the KPI status editor so a stale schema/threshold can't
     *  serialize or block save (that machinery is count-only and the gauge has its own bands). */
    displayTypeKey: {
      get() {
        return this.isGaugeType ? 'gauge' : this.config.messageType;
      },
      set(key) {
        // Clear any prior notice; a fresh switch re-evaluates what (if anything) it clears.
        this.typeSwitchNotice = '';
        if (key === 'gauge') {
          // A gauge carries its own bands, so the count KPI status editor is reset. Observe whether
          // that actually discards a user setting BEFORE clearing, purely to word the inline notice
          // — the reset itself is unchanged.
          const clearedStatus = this.config.statusMode !== STATUS_MODE.AUTO ||
            this.kpiHasValue(this.config.cautionValue) ||
            this.kpiHasValue(this.config.criticalValue) ||
            !!this.config.schemaId ||
            !!this.config.tone;
          this.config.messageType = 'count';
          this.config.kpiVariant = 'gauge';
          this.config.statusMode = STATUS_MODE.AUTO;
          this.config.cautionValue = null;
          this.config.criticalValue = null;
          this.config.schemaId = '';
          this.config.tone = '';
          // A non-gauge widget persists gauge:null, so guard before seeding starter bands.
          if (!this.config.gauge || typeof this.config.gauge !== 'object') {
            this.config.gauge = { min: 0, max: 100, bands: [] };
          }
          if (!Array.isArray(this.config.gauge.bands) || !this.config.gauge.bands.length) {
            this.config.gauge = { ...this.config.gauge, bands: this.defaultGaugeBands() };
          }
          if (clearedStatus) {
            this.typeSwitchNotice = 'Switching to a gauge reset the previous status and colour settings.';
          }
        } else {
          this.config.messageType = key;
          this.config.kpiVariant = '';
          // Switching display type can strand a now-incompatible schema pick (e.g. a category schema
          // chosen for a pie, then switched to a line, or any schema switched to a matrix type that
          // has no schema colors). Drop it back to the default palette so a hidden/re-shown schema
          // control can't block save on a stale, invalid selection. resetSchemaIfIncompatible reports
          // whether it actually cleared one, so we can surface the lighter inline notice.
          if (this.resetSchemaIfIncompatible()) {
            this.typeSwitchNotice = 'Switching display type cleared a colour schema that no longer applies.';
          }
        }
      },
    },

    /** Gauge range validation (null when valid): both bounds required and max > min. Blocks save. */
    gaugeConfigError() {
      if (!this.isGaugeType) return null;
      const min = this.kpiNumber(this.config.gauge && this.config.gauge.min);
      const max = this.kpiNumber(this.config.gauge && this.config.gauge.max);
      if (min === null || max === null) return 'Set a minimum and maximum for the gauge.';
      if (min >= max) return 'Gauge maximum must be greater than the minimum.';
      return null;
    },

    /** The gauge config actually serialized (gauge variant only): normalized min/max + a fresh copy
     *  of the bands, or null when not a gauge. Shared by the saved payload AND the live preview so
     *  they cannot diverge. */
    gaugeSerialized() {
      if (!this.isGaugeType) return null;
      const g = this.config.gauge || {};
      return {
        min: this.kpiNumber(g.min),
        max: this.kpiNumber(g.max),
        bands: Array.isArray(g.bands) ? g.bands.map(b => ({ ...b })) : [],
      };
    },

    // ── Datagrid default order (A2) ────────────────────────────────────────────────
    /** Selected columns as { name, displayName } for the datagrid sort-by picker. */
    datagridSortColumns() {
      return this.selectedColumnsWithTypes.map(c => ({ name: c.name, displayName: c.displayName || c.name }));
    },
    /** Selected NUMERIC columns for the datagrid Top/Bottom rank-by picker (matches the runtime
     *  filter menu's numeric set). */
    datagridNumericColumns() {
      const numericTypes = ['int', 'decimal', 'float', 'bigint', 'smallint', 'numeric', 'money'];
      return this.selectedColumnsWithTypes
        .filter(c => numericTypes.includes(c.dataType))
        .map(c => ({ name: c.name, displayName: c.displayName || c.name }));
    },
    /** Persisted default sort for a datagrid (null otherwise). The grid applies it on load unless a
     *  runtime sort (column-header click or the filter menu) overrides. */
    tableSortSerialized() {
      if (this.config.messageType !== 'datagrid') return null;
      const s = this.config.sort || {};
      if (!s.column) return null;
      return { column: s.column, direction: s.direction === 'desc' ? 'desc' : 'asc' };
    },
    /** Persisted default row cap for a datagrid (null otherwise): Top/Bottom N by a numeric column
     *  (the "shortest remaining life" action list = bottom N). Shares the sqlViewConfig.topBottom key
     *  with the matrix bake, which wins when both apply (a widget is never both). */
    tableTopBottomSerialized() {
      if (this.config.messageType !== 'datagrid') return null;
      const tb = this.config.topBottom || {};
      const count = this.kpiNumber(tb.count);
      if (!tb.mode || !tb.column || count === null || count <= 0) return null;
      return { mode: tb.mode === 'bottom' ? 'bottom' : 'top', count, column: tb.column };
    },

    /** Contextual caption under the aggregation builder's Group by select. Matrix types explain
     *  the slot ordering; other types show nothing (empty string → the hint is hidden). */
    groupByHint() {
      if (this.config.messageType === STACKED_COLUMN) {
        return 'First column is the category (X axis); the second is the series to stack by.';
      }
      if (this.config.messageType === DUAL_AXIS_LINE) {
        return 'Group by one column — the shared X axis. Each calculation below becomes a line.';
      }
      if (this.config.messageType === HEATMAP) {
        return 'First column is the X axis; the second is the Y axis. The calculation colors each cell.';
      }
      if (this.config.messageType === MIXED_COMPARISON) {
        return 'Group by one or more identity columns. Their values are joined into one comparison label.';
      }
      return '';
    },

    /** The aggregation actually persisted / previewed for matrix types: metrics get a generated,
     *  unique alias ({fn}_{column} with _2/_3 de-dupe, and never colliding — case-insensitively —
     *  with a group-by column name) so the raw dict rows key unambiguously (Codex R1-2). Non-matrix
     *  types pass the working aggregation through by reference, so their payload is byte-identical. */
    matrixAggregation() {
      if (!this.isMatrixType) return this.config.aggregation;
      const agg = this.config.aggregation || { groupBy: [], metrics: [] };
      const groupBy = Array.isArray(agg.groupBy) ? agg.groupBy : [];
      const metrics = Array.isArray(agg.metrics) ? agg.metrics : [];
      // Seed the taken set with group-by names (lowercased) so a generated alias can never collide
      // with a group-by column — SQL identifiers are case-insensitive, so the dict key would clash.
      const taken = new Set(groupBy.map(g => String(typeof g === 'string' ? g : (g && (g.column || g.name))).toLowerCase()));
      const outMetrics = metrics.map(m => {
        const base = slugifyAlias(m.function, m.column);
        let alias = base;
        let n = 2;
        while (taken.has(alias.toLowerCase())) {
          alias = `${base}_${n}`;
          n++;
        }
        taken.add(alias.toLowerCase());
        return { ...m, alias };
      });
      return { groupBy, metrics: outMetrics };
    },

    /** Baked TopBottom for matrix types (null otherwise): raises the server's aggregated row cap
     *  100 → 1000 and gives prepareMatrixData the same cap to test its truncation sentinel against
     *  (Codex R1-5). COUNT-ONLY, no column/mode: with topBottom.Column absent the backend skips the
     *  alias-lookup branch (SqlViewQueryService.cs:1321) and falls through to its deterministic
     *  default ORDER BY [firstMetricAlias] DESC, groupBy[0] ASC (:1347-1356) — valid SQL, largest
     *  groups first. Baking the metric ALIAS as the column would instead degrade to a literal
     *  ORDER BY [alias] that SQL Server rejects unless it is also a GROUP BY column (:1324-1332). */
    matrixTopBottom() {
      if (!this.isMatrixType) return null;
      // A mixed comparison owns a real server-side Top/Bottom rank (for example Bottom 20 measured
      // thickness). Preserve it exactly; only the pivot-style matrix charts need the generic cap.
      if (this.isComparisonType) {
        const tb = this.config.topBottom;
        return tb && tb.mode && tb.column && Number(tb.count) > 0 ? { ...tb, count: Number(tb.count) } : null;
      }
      return { count: MATRIX_ROW_CAP };
    },

    comparisonSerialized() {
      if (!this.isComparisonType) return null;
      const alias = String((this.config.comparison && this.config.comparison.referenceMetricAlias) || '').trim();
      return alias ? { referenceMetricAlias: alias } : {};
    },

    /** Saved-filter validation (null when valid): a 'between' filter needs both ends. Blocks the
     *  Data step's Continue and save. */
    filterConfigError() {
      return betweenFilterError(this.config.filters);
    },

    /** Structural validation for matrix types (null when valid). Stacked column needs two group-by
     *  columns (category + series to stack by); dual-axis line needs exactly one group-by (the shared
     *  X axis) and ≥2 calculations (one series per axis). Blocks save with a clear message. */
    matrixConfigError() {
      if (!this.isMatrixType) return null;
      const agg = this.config.aggregation || {};
      const groupBy = Array.isArray(agg.groupBy) ? agg.groupBy : [];
      const metrics = Array.isArray(agg.metrics) ? agg.metrics : [];
      if (this.config.messageType === STACKED_COLUMN && groupBy.length < 2) {
        return 'Stacked column needs a second Group by column to stack by.';
      }
      if (this.config.messageType === DUAL_AXIS_LINE) {
        if (groupBy.length !== 1) return 'Dual-axis line needs exactly one Group by column (the shared X axis).';
        if (metrics.length < 2) return 'Dual-axis line needs at least two calculations — one per axis.';
      }
      if (this.config.messageType === HEATMAP) {
        if (groupBy.length !== 2) return 'Heatmap needs exactly two Group by columns (the X and Y axes).';
        if (metrics.length < 1) return 'Heatmap needs one calculation for the cell values.';
      }
      if (this.config.messageType === MIXED_COMPARISON) {
        if (groupBy.length < 1) return 'Comparison needs at least one Group by column.';
        if (metrics.length < 2) return 'Comparison needs at least two calculations.';
        const reference = String((this.config.comparison && this.config.comparison.referenceMetricAlias) || '').trim();
        if (reference) {
          const aliases = ((this.matrixAggregation && this.matrixAggregation.metrics) || []).map(m => m.alias);
          if (!aliases.includes(reference)) return 'The reference calculation is no longer available. Choose another one.';
        }
      }
      return null;
    },

    /** Serialized per-metric axis assignment for dual-axis line (null otherwise): { alias: 'left' |
     *  'right' } keyed by the GENERATED metric alias (so it matches the builder's series names).
     *  Reads the working config.axisByMetric (also keyed by alias — the keys survive a save/reload
     *  because alias generation is deterministic), defaulting metric[0]→left, metric[1]→right, the
     *  rest→left. Feeds the payload AND the preview so they cannot diverge. */
    matrixAxisByMetric() {
      if (!this.isDualAxisType) return null;
      const metrics = this.dualAxisMetrics;
      const working = this.config.axisByMetric || {};
      const map = {};
      metrics.forEach((m, i) => {
        const explicit = working[m.alias];
        map[m.alias] = explicit === 'right' ? 'right'
          : (explicit === 'left' ? 'left' : (i === 1 ? 'right' : 'left'));
      });
      return map;
    },

    /** Serialized per-metric line styling for dual-axis line (null otherwise): { alias: { color:
     *  '#RRGGBB'|null, lineType: 'solid'|'dashed'|'dotted' } } keyed by the GENERATED metric alias
     *  (matches the builder's series names, like matrixAxisByMetric). Reads the working
     *  config.seriesStyles and emits an entry ONLY when it diverges from the defaults (a set color or
     *  a non-solid line), so an untouched dual-axis widget serializes {} and the builder renders it
     *  byte-identically (palette color + solid line). Feeds the payload AND the preview so they
     *  cannot diverge. */
    matrixSeriesStyles() {
      if (!this.isDualAxisType) return null;
      const working = this.config.seriesStyles || {};
      const map = {};
      this.dualAxisMetrics.forEach((m) => {
        const s = working[m.alias] || {};
        const color = typeof s.color === 'string' && s.color.trim() ? s.color.trim() : null;
        const lineType = (s.lineType === 'dashed' || s.lineType === 'dotted') ? s.lineType : 'solid';
        if (color || lineType !== 'solid') map[m.alias] = { color, lineType };
      });
      return map;
    },

    /** Chart and KPI widgets are meaningless without a metric to plot/count */
    requiresMetric() {
      return this.isChartType || this.config.messageType === 'count';
    },

    /** A count/KPI widget needs only a metric (an ungrouped total); charts still need a
     *  group-by (hasAggregation) so the metric has groups to plot. */
    hasMetric() {
      const metrics = this.config.aggregation && this.config.aggregation.metrics;
      return !!(metrics && metrics.length > 0 &&
        (this.config.messageType === 'count' || this.hasAggregation));
    },

    /** Inline guidance inside Group Results while a chart/KPI type has no usable metric.
     *  Count/KPI widgets just need a calculation (an ungrouped total). For charts a
     *  calculation only counts once a group-by column exists (hasMetric), so the message
     *  flips when calculations exist but their grouping was removed. */
    metricHintText() {
      if (this.config.messageType === 'count') {
        return 'Choose what this KPI counts, for example Count of records.';
      }
      const metrics = this.config.aggregation && this.config.aggregation.metrics;
      if (metrics && metrics.length && !this.hasAggregation) {
        return 'Group by a column so the calculation has groups to plot.';
      }
      const names = {
        count: 'KPI widgets',
        bar: 'Bar charts',
        column: 'Column charts',
        line: 'Line charts',
        area: 'Area charts',
        pie: 'Pie charts',
        donut: 'Donut charts',
        [STACKED_COLUMN]: 'Stacked column charts',
        [DUAL_AXIS_LINE]: 'Dual-axis line charts',
      };
      const name = names[this.config.messageType];
      return name
        ? `${name} need at least one calculation to plot.`
        : 'This display type needs at least one calculation to plot.';
    },

    canProceed() {
      // Gates the Data step's Continue (source + at least one column). Later steps gate through
      // canAdvance / canConfirm.
      return this.config.tableName && this.config.columns.length > 0;
    },

    /** Per-step gate for the footer's Continue button (Data -> Build, Build -> Behavior). The
     *  final step has no Continue — it confirms through canConfirm instead. */
    canAdvance() {
      if (this.step === 0) return !!this.canProceed && !this.filterConfigError;
      // Build visualization -> Behavior & finish: a chart/KPI needs its metric, and matrix types
      // need the right group-by/metric shape (both are configured on this step).
      if (this.step === 1) return (!this.requiresMetric || this.hasMetric) && !this.matrixConfigError;
      return false;
    },

    canConfirm() {
      // Create lives on step 2, but the rail lets a user land there with the data
      // step incomplete — so re-check everything or an empty chart/KPI ships (B7).
      // A bad KPI threshold pair (count Auto mode, both set, wrong order) also blocks save, as
      // does an incompletely configured or invalid schema (count Schema mode).
      return this.config.tableName &&
        this.config.columns.length > 0 &&
        (!this.requiresMetric || this.hasMetric) &&
        // The KPI threshold/schema errors are count-KPI-card only; a gauge has its own bands, so
        // they never gate it (a stale one from a type-switch must not block gauge save).
        (this.isGaugeType || (!this.kpiThresholdError && !this.kpiSchemaError)) &&
        !this.gaugeConfigError &&
        !this.matrixConfigError &&
        !this.filterConfigError &&
        !this.spatialFocusStorageDecision.error;
    },

    /** Human-readable reason the primary action is blocked, shown in the footer. */
    blockReason() {
      // Data (step index 0): filters are configured here, so an incomplete one gates Continue.
      if (this.step === 0) return this.filterConfigError;
      // Build visualization (step index 1): a chart/KPI needs a calculation, and matrix types need
      // the right group-by/metric shape — both are configured here, so they gate Continue.
      if (this.step === 1) {
        if (this.requiresMetric && !this.hasMetric) return 'Add at least one calculation to continue.';
        if (this.matrixConfigError) return this.matrixConfigError;
        return null;
      }
      // Behavior & finish (step index 2, where Create lives): the appearance-level validation (gauge
      // range, KPI thresholds/schema) blocks save. Re-surface the upstream metric/matrix block too,
      // since the rail can land a user here with the Build step incomplete. A count is in exactly one
      // status mode, so these never collide.
      if (this.step === 2) {
        if (this.requiresMetric && !this.hasMetric) return 'Add at least one calculation to continue.';
        if (this.gaugeConfigError) return this.gaugeConfigError;
        if (!this.isGaugeType && this.kpiThresholdError) return this.kpiThresholdError;
        if (!this.isGaugeType && this.kpiSchemaError) return this.kpiSchemaError;
        if (this.matrixConfigError) return this.matrixConfigError;
        if (this.filterConfigError) return this.filterConfigError;
        if (this.spatialFocusStorageDecision.error) {
          return this.spatialFocusStorageDecision.error.message;
        }
      }
      return null;
    },

    previewConfig() {
      return {
        tableName: this.config.tableName,
        // Friendly source name so the preview's builder-v1 subtitle reads
        // "Anomaly Register · ..." instead of the raw vw_Register_... view name.
        tableDisplayName: this.config.tableDisplayName,
        columns: this.config.columns,
        columnTypes: this.config.columnTypes,
        columnDisplayNames: this.config.columnDisplayNames,
        // Matrix types preview through the generated-alias aggregation (byte-identical passthrough
        // for every other type) + carry stackMode / baked TopBottom / axisByMetric so the preview
        // matrix shapes, its cap-sentinel, and the dual-axis mapping behave like the saved widget.
        // A count/gauge previews its single total, without the hidden group-by, as it is saved.
        aggregation: aggregationForType(this.matrixAggregation, this.config.messageType),
        stackMode: this.isMatrixType ? (this.config.stackMode || 'stack') : null,
        topBottom: this.matrixTopBottom,
        axisByMetric: this.matrixAxisByMetric,
        // Dual-axis per-metric line styling so the preview reflects a colour / line-type edit live,
        // through the same builder the saved widget uses (same shared computed as the payload).
        seriesStyles: this.matrixSeriesStyles,
        comparison: this.comparisonSerialized,
        filters: this.config.filters,
        link3D: this.config.link3D,
        // Mirrors the saved payload's sqlViewConfig.widgetSize (the canonical persistence home,
        // §buildWidgetPayload) so a consumer reading message.sqlViewConfig.widgetSize sees the
        // same live value as message.widgetSize below - the preview never disagrees with itself
        // regardless of which of the two the consumer reads.
        widgetSize: this.config.widgetSize,
        // KPI icon so the count preview reflects the picker live (A+ card render).
        icon: this.config.icon,
        // Manual tone / unit / footnote so the count preview reflects them live too. Tone is the
        // FIXED-colour value only (Auto mode -> '' via kpiSerializedTone); thresholds carry the
        // dynamic status. Both feed dashboard-widget.kpiTone, so the preview matches what ships.
        tone: this.kpiSerializedTone,
        unit: this.config.unit,
        footnote: this.config.footnote,
        // Gauge variant so the live preview renders the dial through the SAME widget component as
        // the board; null for a normal KPI. Built from the same shared computed as the payload.
        kpiVariant: this.config.kpiVariant || '',
        gauge: this.gaugeSerialized,
        thresholds: this.kpiThresholds,
        // Schema coloring (count Schema mode) so the live preview tile colours through the SAME
        // widget component as the board; null in Auto/Fixed → the legacy path. Built from the same
        // shared computeds as the saved payload, so preview and save cannot diverge.
        coloringSource: this.kpiSerializedColoringSource,
        schemaSelection: this.kpiSchemaSelection,
      };
    },

    /** Legacy fontSize mirror for the live preview, derived fresh from config.widgetSize (the
     *  widget-size toggle's v-model) on every read - config.fontSize itself stops updating the
     *  moment the toggle is used, so binding the preview to it directly would show a stale size. */
    previewFontSize() {
      return LEGACY_FONT_SIZE_BY_WIDGET_SIZE[this.config.widgetSize];
    },

    /**
     * Serialize the KPI status editor state (count Auto mode) into a sqlViewConfig.thresholds
     * object, or null when it should be ABSENT. Shared by the live preview and the saved payload
     * so the preview matches what ships. Rules are emitted only for the boundaries the user
     * actually set; the op is derived from direction (single source of truth: higherIsWorse->gte,
     * lowerIsWorse->lte); default is hardcoded 'good'. Both boundaries blank -> null (never
     * {rules:[]}, which would paint permanently good). Fixed mode / non-count -> null (manual
     * tone wins there).
     */
    kpiThresholds() {
      if (this.config.messageType !== 'count' || this.config.statusMode !== STATUS_MODE.AUTO) return null;
      const caution = this.kpiNumber(this.config.cautionValue);
      const critical = this.kpiNumber(this.config.criticalValue);
      if (caution === null && critical === null) return null;
      const op = this.config.direction === KPI_DIRECTION.LOWER_IS_WORSE ? 'lte' : 'gte';
      const rules = [];
      if (critical !== null) rules.push({ op, value: critical, tone: TONE.CRITICAL });
      if (caution !== null) rules.push({ op, value: caution, tone: TONE.CAUTION });
      return {
        direction: this.config.direction === KPI_DIRECTION.LOWER_IS_WORSE ? KPI_DIRECTION.LOWER_IS_WORSE : KPI_DIRECTION.HIGHER_IS_WORSE,
        rules,
        default: TONE.GOOD,
      };
    },

    /** The manual fixed-colour tone actually serialized: only in Fixed mode. Auto mode's colour
     *  comes from thresholds, so a lingering manual tone would be a stale lower-priority fallback;
     *  clear it to '' there. */
    kpiSerializedTone() {
      // Only Fixed mode serializes a manual tone; Auto uses thresholds and Schema uses the
      // schemaSelection, so clear any stale tone in those modes.
      if (this.config.messageType === 'count') {
        return this.config.statusMode === STATUS_MODE.FIXED ? (this.config.tone || '') : '';
      }
      return this.config.tone || '';
    },

    /** True once the user has set at least one boundary (Auto mode) — flips the
     *  "no status set yet" hint off. */
    kpiHasThreshold() {
      return this.kpiHasValue(this.config.cautionValue) || this.kpiHasValue(this.config.criticalValue);
    },

    /** Direction-aware hint under the Caution field. */
    kpiCautionHint() {
      return this.config.direction === KPI_DIRECTION.LOWER_IS_WORSE
        ? 'At or below this number, the tile turns caution.'
        : 'At or above this number, the tile turns caution.';
    },

    /** Direction-aware hint under the Critical field. */
    kpiCriticalHint() {
      return this.config.direction === KPI_DIRECTION.LOWER_IS_WORSE
        ? 'At or below this number, the tile turns critical.'
        : 'At or above this number, the tile turns critical.';
    },

    /** Inline threshold validation (count Auto mode): a message only when BOTH boundaries are set
     *  and critical is not more extreme than caution for the chosen direction; null when valid.
     *  Wired into canConfirm / blockReason so it blocks save. */
    kpiThresholdError() {
      if (this.config.messageType !== 'count' || this.config.statusMode !== STATUS_MODE.AUTO) return null;
      const caution = this.kpiNumber(this.config.cautionValue);
      const critical = this.kpiNumber(this.config.criticalValue);
      if (caution === null || critical === null) return null; // only when BOTH set
      if (this.config.direction === KPI_DIRECTION.LOWER_IS_WORSE) {
        return critical < caution ? null : 'Critical should be a lower number than caution.';
      }
      return critical > caution ? null : 'Critical should be a higher number than caution.';
    },

    // ── Coloring-schema selection (count 'schema' status + chart 'schema' series colors; §4.1/§4.3/§5) ──
    // SELECT-ONLY: the dropdown offers built-in schemas + the current tab's library schemas (the
    // librarySchemas prop). Inline schemas are no longer creatable here. Working state is
    // config.schemaId + schemaValueUnit; kpiSchemaSelection serializes coloringSource +
    // schemaSelection, feeding BOTH the saved payload and the live preview so they cannot diverge.

    /** True when the widget is in schema-coloring mode: a count KPI on 'By schema', or a chart on
     *  'By schema' series colors. Gates every schema serializer / validator below. */
    schemaColoringActive() {
      if (this.config.messageType === 'count') return this.config.statusMode === STATUS_MODE.SCHEMA;
      // Matrix chart builders (stacked column, dual-axis line) don't consume schema colors, so schema
      // mode never activates for them - this keeps a stale mode from blocking save on a hidden control.
      if (this.isChartType) return !this.isMatrixType && this.config.chartColoringMode === CHART_COLORING_MODE.SCHEMA;
      return false;
    },

    /** True for the single-series chart types whose ECharts builder actually consumes schema colors
     *  (bar/column/pie/donut/line/area). The matrix builders (stacked column, dual-axis line) cycle
     *  the categorical palette / per-metric styles instead, so the "By schema" series-color control is
     *  hidden for them - offering it would be a no-op that dead-ends save on an empty schema list. */
    chartSupportsColorSchema() {
      return this.isChartType && !this.isMatrixType;
    },

    /** Which schema forms apply to this widget's binding (§4.2/§4.6): a count value and a trend are
     *  numeric; a pie/donut is grouped by a label; a bar/column can be coloured by its group label
     *  OR its measure value, so it offers both. Filters both built-ins and library schemas. */
    applicableSchemaKinds() {
      const t = this.config.messageType;
      if (t === 'count' || t === 'line' || t === 'area') return [APPLIES_TO.NUMBER];
      if (t === 'pie' || t === 'donut') return [APPLIES_TO.CATEGORY];
      if (t === 'bar' || t === 'column') return [APPLIES_TO.CATEGORY, APPLIES_TO.NUMBER];
      return [];
    },

    /** The tab's library schemas as a safe array ([] while the prop is null/loading). */
    kpiSchemaLibrary() {
      return Array.isArray(this.librarySchemas) ? this.librarySchemas : [];
    },

    /** True while the library is still loading (prop null) and schema mode is active — the dropdown
     *  shows a loading item and a restored reference is treated as pending, not missing. */
    kpiSchemaLibraryLoading() {
      return this.schemaColoringActive && this.librarySchemas === null;
    },

    /** "Default inspection bands" badge shows only for a NUMBER built-in (the CR/RL/RT inspection
     *  scales); a category built-in or a library schema is not facility inspection bands. */
    kpiSchemaShowBandBadge() {
      const s = this.kpiSelectedBuiltIn;
      return !!(s && s.appliesTo === APPLIES_TO.NUMBER);
    },

    /** True when the schema picker should show its detail block (band preview / value-unit / custom
     *  hint): a resolved schema is selected, or a restored legacy inline. Passed to SchemaPicker. */
    kpiSchemaShowDetails() {
      return !!(this.kpiSelectedSchema || this.kpiSchemaIsCustom);
    },

    /** Schema dropdown options: applicable built-ins + (once loaded) the tab's applicable library
     *  schemas; a disabled "Loading schemas…" item while the prop is null. The current selection
     *  always gets a matching item so v-select never renders blank — a restored legacy inline, a
     *  reference still loading, or a reference whose schema is now missing. */
    kpiSchemaSelectItems() {
      const kinds = this.applicableSchemaKinds;
      const items = Object.values(BUILT_IN_SCHEMAS)
        .filter(s => kinds.includes(s.appliesTo))
        .map(s => ({ text: s.name, value: s.id }));
      if (this.kpiSchemaLibraryLoading) {
        items.push({ text: 'Loading schemas…', value: '__loading__', disabled: true });
      } else {
        this.kpiSchemaLibrary
          .filter(s => s && kinds.includes(s.appliesTo))
          .forEach(s => items.push({ text: s.name, value: s.id }));
      }
      const id = this.config.schemaId;
      if (id && !items.some(it => it.value === id)) {
        if (id === CUSTOM_SCHEMA_ID) items.push({ text: 'Custom schema (this widget)', value: id });
        else if (this.kpiSchemaLibraryLoading) items.push({ text: 'Loading schema…', value: id, disabled: true });
        else items.push({ text: `Missing schema (${id})`, value: id });
      }
      return items;
    },

    /** The selected built-in schema object, or null when nothing / a library / a legacy inline is chosen. */
    kpiSelectedBuiltIn() {
      if (!this.config.schemaId || this.config.schemaId === CUSTOM_SCHEMA_ID) return null;
      return getBuiltInSchema(this.config.schemaId);
    },

    /** The selected LIBRARY (dashboard) schema object for config.schemaId, or null. */
    kpiSelectedLibrarySchema() {
      const id = this.config.schemaId;
      if (!id || id === CUSTOM_SCHEMA_ID) return null;
      return this.kpiSchemaLibrary.find(s => s && s.id === id) || null;
    },

    /** The resolved schema object behind the current pick — built-in OR library — for the value-unit
     *  selector and the band preview. Null for a legacy inline (previewed from its stash) or a
     *  missing/loading reference. */
    kpiSelectedSchema() {
      return this.kpiSelectedBuiltIn || this.kpiSelectedLibrarySchema;
    },

    /** True when the widget carries a restored LEGACY inline schema (no longer creatable). */
    kpiSchemaIsCustom() {
      return this.config.schemaId === CUSTOM_SCHEMA_ID;
    },

    /** True when the current selection is a reference whose schema is genuinely gone: the library is
     *  LOADED (not null) and the id is neither a built-in, the legacy-inline sentinel, nor present in
     *  the library. Never true while loading — a null library renders as pending, not missing. */
    kpiSchemaMissingReference() {
      if (!this.schemaColoringActive || this.librarySchemas === null) return false;
      const id = this.config.schemaId;
      if (!id || id === CUSTOM_SCHEMA_ID || getBuiltInSchema(id)) return false;
      return !this.kpiSchemaLibrary.some(s => s && s.id === id);
    },

    /** Value-unit options for a unit-bearing schema (built-in or library): every unit that converts
     *  to the schema's canonical unit (so e.g. a years KPI can ride a months schema). >1 → shown. */
    kpiSchemaUnitItems() {
      const s = this.kpiSelectedSchema;
      if (!s || !s.unit) return [];
      return Object.values(UNIT)
        .filter(u => canConvert(u, s.unit))
        .map(u => ({ text: u, value: u }));
    },

    /** The schema definition shown in the read-only band preview: the resolved built-in/library
     *  schema, or a legacy inline's stashed definition. */
    kpiSchemaPreviewDefinition() {
      if (this.kpiSchemaIsCustom) return this.legacyInlineSelection && this.legacyInlineSelection.definition;
      return this.kpiSelectedSchema;
    },

    /** Read-only preview of the selected schema's bands: one swatch + label per range/entry, for a
     *  number schema (ranges) or a category schema (entries). Null when there is nothing to preview. */
    kpiSchemaBandPreview() {
      const s = this.kpiSchemaPreviewDefinition;
      if (!s) return null;
      if (s.appliesTo === APPLIES_TO.CATEGORY && Array.isArray(s.entries)) {
        return s.entries.map(e => ({ label: e.label || String(e.value), swatch: this.schemaPaintSwatch(e) }));
      }
      if (s.appliesTo === APPLIES_TO.NUMBER && Array.isArray(s.ranges) && s.ranges.length) {
        return s.ranges.map(r => ({ label: r.label || this.schemaRangeLabel(r, s.unit), swatch: this.schemaPaintSwatch(r) }));
      }
      return null;
    },

    /** The serialized schemaSelection (§4.3), or null when not in schema mode / nothing chosen.
     *  Branch order: a restored legacy inline re-emits its STASHED original VERBATIM (never rebuilt —
     *  a rebuild would drift the unit/id); a built-in → { kind:'builtin', schemaId, followLatest,
     *  valueUnit? }; anything else → a library reference { kind:'reference', schemaId, followLatest,
     *  valueUnit? }. A reference serializes even when its schema is missing/loading — the pick is
     *  never silently dropped; the missing-state gate blocks the save instead. Feeds payload + preview. */
    kpiSchemaSelection() {
      if (!this.schemaColoringActive) return null;
      const id = this.config.schemaId;
      if (!id) return null;
      if (id === CUSTOM_SCHEMA_ID) return this.legacyInlineSelection || null;
      const builtIn = getBuiltInSchema(id);
      if (builtIn) {
        const selection = { kind: SCHEMA_KIND.BUILTIN, schemaId: id, followLatest: true };
        if (builtIn.unit) selection.valueUnit = this.config.schemaValueUnit || builtIn.unit;
        return selection;
      }
      const selection = { kind: SCHEMA_KIND.REFERENCE, schemaId: id, followLatest: true };
      const lib = this.kpiSelectedLibrarySchema;
      // Prefer the restored/selected unit (it survives a still-loading library), then the schema's
      // own unit — so a reference round-trips its valueUnit even before the library resolves.
      const valueUnit = this.config.schemaValueUnit || (lib && lib.unit) || '';
      if (valueUnit) selection.valueUnit = valueUnit;
      return selection;
    },

    /** coloringSource actually serialized: 'schema' when schema coloring is active (count or chart),
     *  else null. Static / Auto / Fixed never write coloringSource — an absent key keeps the shipped
     *  legacy path byte-identical (count KPI evaluator AND chart option object). */
    kpiSerializedColoringSource() {
      return this.schemaColoringActive ? COLORING_SOURCE.SCHEMA : null;
    },

    /** Any reason schema mode is incompletely configured: no schema chosen, or a reference whose
     *  schema was removed. Null otherwise (a legacy inline is valid + read-only; a loading reference
     *  is pending, not blocked). Blocks save via canConfirm / blockReason. */
    kpiSchemaError() {
      if (!this.schemaColoringActive) return null;
      if (!this.config.schemaId) return 'Choose a color schema to continue.';
      if (this.kpiSchemaMissingReference) return 'This schema was removed. Pick another color schema.';
      return null;
    },
  },

  watch: {
    // Each step opens on its Config pane; the user toggles to Preview per step.
    step() {
      this.contentView = 'config';
    },

    // Reopen always lands on Config. The per-step watcher above only fires on a step
    // *change*, so closing on Preview then reopening on the same step (0) would keep
    // Preview. Reset whenever the dialog opens — covers every open path.
    'modal.active'(open) {
      if (open) this.contentView = 'config';
    },

    // Register POI (Step 4): refetch the current view's link declarations whenever the resolved
    // register id changes — a view switch, or the view list arriving async after open (null → id).
    // Same value → no fire (same register, declarations already loaded).
    poiRegisterId() {
      this.fetchPoiDeclarations();
    },
  },

  methods: {
    defaultConfig() {
      return {
        tableName: null,
        tableDisplayName: null,
        columns: [],
        columnTypes: {},
        columnDisplayNames: {},
        messageType: 'datagrid',
        aggregation: { groupBy: [], metrics: [] },
        filters: [],
        link3D: false,
        tagColumn: null,
        // undefined preserves legacy fallback; null is an explicit server-owned "not linkable".
        tagLink: undefined,
        // Same presence contract as tagLink: selected catalogue capability, or explicit null.
        spatial: undefined,
        // 3D colour-coding: which column drives element colour + a value→hex enum map
        coloring: { column: null, valueColors: {} },
        title: '',
        subTitle: '',
        aboutText: '',
        fontSize: 1,
        // Canonical widget-density level (small|normal|large); fontSize above is now only the
        // legacy mirror kept for round-trip safety. New widgets default to 'normal' regardless
        // of display type - the family-aware legacy default (datagrid -> large) only applies
        // when RESOLVING an existing widget that predates this control (see open() below).
        widgetSize: 'normal',
        // Optional KPI icon (count widgets): an MDI name persisted through sqlViewConfig;
        // '' = none (default). Rendered only on the builder-v1 A+ KPI card.
        icon: '',
        // Optional KPI severity tone / unit / footnote (count widgets): all persisted through
        // sqlViewConfig and rendered only on the builder-v1 A+ card. '' = default.
        tone: '',
        unit: '',
        footnote: '',
        // KPI dynamic status editor state (count widgets). This is the wizard's WORKING state,
        // not the serialized shape: buildWidgetPayload turns it into sqlViewConfig.thresholds
        // (Auto mode, >=1 boundary) or leaves the manual `tone` (Fixed mode); open() rebuilds it
        // from a saved thresholds object. Defaults = Auto mode, higher-is-worse, both boundaries
        // blank -> no thresholds emitted -> neutral tile.
        statusMode: STATUS_MODE.AUTO,           // auto (thresholds) | fixed (manual tone) | schema (range schema)
        direction: KPI_DIRECTION.HIGHER_IS_WORSE, // higherIsWorse -> gte | lowerIsWorse -> lte
        cautionValue: null,           // number | null (blank = not set)
        criticalValue: null,          // number | null (blank = not set)
        // KPI coloring-schema selection state (count 'schema' status + chart 'schema' series colors).
        // Working state only; buildWidgetPayload serializes it into sqlViewConfig.coloringSource +
        // schemaSelection (open() rebuilds it from a saved selection). schemaId is a built-in id, a
        // library schema uuid, or CUSTOM_SCHEMA_ID for a restored legacy inline. Select-only — the
        // wizard no longer creates inline schemas. Default = nothing chosen.
        schemaId: '',
        schemaValueUnit: '',
        // Chart series-coloring mode (chart types only): 'static' (today's palette / severity
        // behavior) | 'schema' (a range schema). Charts have no 'direction' (that is single-value
        // KPI semantics, §4.1). Shares schemaId with the count path — a widget is one type at a
        // time. Default 'static' → coloringSource absent → chart render byte-identical.
        chartColoringMode: CHART_COLORING_MODE.STATIC,
        // Stacked-column layout: 'stack' (segments summed to a total) | 'group' (side-by-side).
        // Only meaningful for matrix types (stackedColumn); ignored by every other type.
        stackMode: 'stack',
        // Dual-axis line per-metric axis assignment { generatedAlias: 'left' | 'right' }. Only
        // meaningful for dualAxisLine; serialized via matrixAxisByMetric. Empty = defaults
        // (metric[0] left, metric[1] right).
        axisByMetric: {},
        // Dual-axis line per-metric line styling { generatedAlias: { color, lineType } }. Alias-keyed
        // (mirrors axisByMetric) so it survives a metric add/remove/reorder; serialized via
        // matrixSeriesStyles. Empty = every metric keeps its palette color + a solid line (byte-
        // identical to today).
        seriesStyles: {},
        // Mixed comparison's optional metric alias promoted from grouped columns to a dashed line.
        comparison: { referenceMetricAlias: null },
        // Gauge display variant (count widgets): kpiVariant 'gauge' + a static min/max/bands dial
        // config. '' = a normal KPI number card. Both persisted through sqlViewConfig.
        kpiVariant: '',
        gauge: { min: 0, max: 100, bands: [] },
        legend: { show: true, position: 'bottom' },
        exportEnabled: true,
        columnWidthMode: 'autoFit',
        // Datagrid default order (A2 action-list groundwork): a persisted default sort + row cap the
        // grid applies on load, overridden at runtime by the widget filter menu. Only meaningful for
        // datagrid widgets; serialized to sqlViewConfig.sort / sqlViewConfig.topBottom.
        sort: { column: null, direction: 'asc' },
        topBottom: { mode: null, count: null, column: null },
        refreshIntervalMinutes: null,
      };
    },

    /** Open the wizard (create or edit mode) */
    open(widget) {
      this.modal.active = true;
      this.columnSamples = {};
      this.hiddenColumnNames = [];
      // Clear any stashed legacy inline selection so a prior edit can't leak into this one.
      this.legacyInlineSelection = null;
      // Clear any stale destructive-type-switch notice from a prior session.
      this.typeSwitchNotice = '';
      this.saveErrors = [];
      this.preservedConfig = {};
      this.preservedConfigTable = null;

      if (widget && widget.sqlViewConfig) {
        // Edit mode — populate from existing widget. Land on Behavior & finish: edits are mostly
        // appearance/behavior tweaks and Update lives on that last step; Back reaches Build then Data.
        this.step = 2;
        this.isEditing = true;
        this.editingWidgetId = widget.id;
        const cfg = typeof widget.sqlViewConfig === 'string'
          ? JSON.parse(widget.sqlViewConfig)
          : widget.sqlViewConfig;
        const messageType = widget.messageType || cfg.messageType || 'datagrid';
        this.preservedConfig = preservedConfigEntries(cfg);
        this.preservedConfigTable = cfg.tableName || null;

        this.config = {
          ...this.defaultConfig(),
          ...cfg,
          messageType,
          title: widget.title || '',
          subTitle: widget.subTitle || '',
          fontSize: widget.fontSize ?? 1,
          // Resolve the saved widget's density level. Canonical widgetSize persists inside
          // sqlViewConfig (cfg), not the top-level widget object - cfg.widgetSize is the source
          // of truth; widget.widgetSize is only tolerated as a synthetic-context override (e.g.
          // the wizard's own live preview, which never round-trips through the api). Otherwise
          // fall back through the legacy fontSize, family-aware so a pre-feature datagrid
          // (fontSize never applied to grids) resolves to its actual look, 'large'.
          widgetSize: resolveWidgetSize(
            { widgetSize: cfg.widgetSize ?? widget.widgetSize, fontSize: widget.fontSize },
            { family: messageType === 'datagrid' ? 'datagrid' : undefined },
          ),
          legend: widget.legend || { show: true, position: 'bottom' },
        };
        this.poiSavedColumns = Array.isArray(cfg.columns) ? [...cfg.columns] : [];

        // Normalise colouring: saved widgets store `coloring: null` when 3D colouring is off,
        // which would otherwise override the default object and break `config.coloring.column`.
        if (!this.config.coloring || typeof this.config.coloring !== 'object') {
          this.config.coloring = { column: null, valueColors: {} };
        } else if (!this.config.coloring.valueColors) {
          this.config.coloring.valueColors = {};
        }

        // Same null-override guard for the datagrid default order (A2) and the gauge blob: saved
        // widgets store these as null when off, which would otherwise break the v-model bindings.
        if (!this.config.sort || typeof this.config.sort !== 'object') {
          this.config.sort = { column: null, direction: 'asc' };
        }
        if (!this.config.topBottom || typeof this.config.topBottom !== 'object' || Array.isArray(this.config.topBottom)) {
          this.config.topBottom = { mode: null, count: null, column: null };
        }
        if (!this.config.gauge || typeof this.config.gauge !== 'object') {
          this.config.gauge = { min: 0, max: 100, bands: [] };
        }
        if (!this.config.comparison || typeof this.config.comparison !== 'object') {
          this.config.comparison = { referenceMetricAlias: null };
        }

        // Rebuild the KPI status editor state from the saved config so an existing count widget
        // repaints its Status section exactly. Schema mode (coloringSource 'schema') restores the
        // picked schema / value-unit (or stashes a legacy inline); otherwise the shipped Auto/Fixed
        // restore runs unchanged (an absent coloringSource → legacy path, byte-identical).
        if (cfg.coloringSource === COLORING_SOURCE.SCHEMA) {
          this.restoreSchemaSelection(cfg.schemaSelection);
          // Route the restored schema into the right mode for the widget's type: chart series
          // colors vs the count Status control (restoreKpiStatusState is count-only). Matrix chart
          // types (stacked column, dual-axis line) don't consume schema colors, so they stay on the
          // default palette - a legacy/corrupt selection is dropped, never restored into a hidden,
          // save-blocking control.
          if (this.isChartType) {
            if (!this.isMatrixType) this.config.chartColoringMode = CHART_COLORING_MODE.SCHEMA;
          } else {
            this.config.statusMode = STATUS_MODE.SCHEMA;
          }
        } else {
          this.restoreKpiStatusState(cfg);
        }

        // Load the column list; the chooser reads the selection from config.columns.
        if (cfg.tableName) {
          this.fetchColumns(cfg.tableName);
        }

        // Restore the colour-coding swatches (reload the column's values, keeping saved colours)
        this.coloringValues = [];
        if (this.config.link3D && this.config.coloring?.column) {
          this.fetchColoringValues();
        }
      } else {
        // Create mode starts at the Data step
        this.step = 0;
        this.isEditing = false;
        this.editingWidgetId = null;
        this.config = this.defaultConfig();
        this.poiSavedColumns = null;
        this.availableColumns = [];
      }

      this.fetchViews();
    },

    /**
     * Open the wizard in CREATE mode, pre-seeded with another widget's data source
     * (same view + columns). A chart built on the grid's source auto cross-filters
     * that grid, because same-source siblings share the dashboard's filterSessionId.
     * @param {{ tableName: string, tableDisplayName?: string, columns?: string[], columnTypes?: object, columnDisplayNames?: object }} seed
     */
    openFromSource(seed) {
      if (!seed || !seed.tableName) return;

      this.isEditing = false;
      this.editingWidgetId = null;
      this.poiSavedColumns = null;
      this.preservedConfig = {};
      this.preservedConfigTable = null;
      this.saveErrors = [];
      this.modal.active = true;

      this.config = {
        ...this.defaultConfig(),
        tableName: seed.tableName,
        tableDisplayName: seed.tableDisplayName || null,
        columns: Array.isArray(seed.columns) ? [...seed.columns] : [],
        columnTypes: { ...(seed.columnTypes || {}) },
        columnDisplayNames: { ...(seed.columnDisplayNames || {}) },
        tagLink: seed.tagLink,
        spatial: seed.spatial,
        // Provenance of a chart seeded off a grid (#9): the source grid widget's id, carried into
        // the saved sqlViewConfig.createdFromWidgetId. Consumed defensively - the seed producer
        // (DashboardItem.createChartFromGrid) supplies it separately; undefined until then, which
        // simply leaves the persisted field absent.
        createdFromWidgetId: seed.createdFromWidgetId || undefined,
        // Seeded intent is a chart on this grid's source; the user picks group/metric next.
        messageType: 'bar',
      };

      // Land on Build visualization so the metric gets configured (canAdvance/canConfirm enforce it).
      this.step = 1;
      this.typeSwitchNotice = '';
      this.availableColumns = [];
      this.hiddenColumnNames = [];
      this.columnSamples = {};

      // Load the column list so the picker + aggregation builder repopulate; keep the
      // seeded selection, or fall back to all columns if the seed carried none.
      this.fetchColumns(seed.tableName).then(() => {
        if (!this.config.columns.length && this.availableColumns.length) {
          this.onColumnsChanged(this.availableColumns.map(c => c.name));
        }
      });

      this.fetchViews();
    },

    closeHandler() {
      this.modal.active = false;
    },

    /**
     * Save. The parent persists the widget and reports back through the settle callback:
     * settle() closes the wizard, settle(err) keeps it open and lists what the server rejected
     * (a 400 WIDGET_CONFIG_INVALID carries one readable message per problem).
     */
    confirmHandler() {
      const widget = this.buildWidgetPayload();
      this.saveErrors = [];
      this.modal.loading = true;
      this.$emit('updated', widget, (err) => {
        this.modal.loading = false;
        if (!err) {
          this.modal.active = false;
          return;
        }
        this.saveErrors = widgetSaveErrorMessages(err);
      });
    },

    buildWidgetPayload() {
      const hiddenColumnNames = new Set([
        ...COMPILER_HIDDEN_COLUMNS,
        ...(this.hiddenColumnNames || []).map(name => String(name || '').toLowerCase()),
      ]);
      const isHidden = column => hiddenColumnNames.has(String(column || '').toLowerCase());
      const columns = [...this.config.columns].filter(column => !isHidden(column));
      const selectedView = (this.availableViews || []).find(
        view => String(view?.name).toLowerCase() === String(this.config.tableName || '').toLowerCase(),
      );
      const serverTagLink = selectedView
        && Object.prototype.hasOwnProperty.call(selectedView, 'tagLink')
        ? selectedView.tagLink
        : this.config.tagLink;
      const tagLink = resolveTagLink({
        serverTagLink,
        legacyTagColumn: this.config.tagColumn,
        tableName: this.config.tableName,
        availableColumns: this.availableColumns.length
          ? this.availableColumns
          : this.config.columns,
      });
      const spatialCapability = selectedView
        && Object.prototype.hasOwnProperty.call(selectedView, 'spatial')
        ? selectedView.spatial
        : this.config.spatial;
      const bindingFocus = this.config.link3D && hasSpatialBindingCapability(spatialCapability)
        ? spatialFocusOverrideForStorage(spatialCapability, this.config.spatialFocus).value
        : null;
      const usesBindingFocus = this.config.link3D && hasSpatialBindingCapability(spatialCapability);
      let tagColumn = tagLink?.column || null;

      // Isolation consumes the descriptor's PHYSICAL linkage column. Keep the old virtual TagId
      // alongside register rows so legacy readers remain compatible while the new grid uses Id.
      if (tagLink && this.config.link3D) {
        if (!columns.includes(tagLink.column)) columns.push(tagLink.column);
        if (tagLink.shape === 'registerRows') {
          tagColumn = 'TagId';
          if (!columns.includes('TagId')) columns.push('TagId');
        }
      }

      // 3D colour coding: persist only when Link 3D is on and a column is chosen. Force the
      // colouring column into the queried columns so the grid row always carries its value
      // (the isolate paint reads selectedRowData[coloring.column]); without this a later
      // column deselect would leave a dangling colouring.column that resolves to undefined.
      let coloring = null;
      if (this.config.link3D && this.config.coloring?.column && !isHidden(this.config.coloring.column)) {
        coloring = {
          column: this.config.coloring.column,
          valueColors: { ...(this.config.coloring.valueColors || {}) },
        };
        if (!columns.includes(coloring.column)) columns.push(coloring.column);
      }

      // Register POI (Step 3): force any EXPLICIT highlight/context column into the queried columns
      // so the grid row carries the value the isolate paint resolves against — the same guarantee
      // the coloring.column block above gives. 'inherit' resolves to a declared column the wizard
      // only knows once declarations are bridged (Step 4), so only explicit column names are forced
      // here; absent / 'none' / 'inherit' add nothing.
      [normalizePoiSource(this.config.poiSource), normalizePoiSource(this.config.contextSource)]
        .forEach((src) => {
          if (src && src !== POI_SOURCE_INHERIT && src !== POI_SOURCE_NONE
            && !isHidden(src) && !columns.includes(src)) {
            columns.push(src);
          }
        });

      // Step 4 finishes the 'inherit' path Step 3 deferred: when the effective config resolves via
      // 'inherit', its highlight/context columns are the register's DEFAULT columns — which the raw
      // explicit block above cannot know. computeEffectivePoi is the single home of the precedence,
      // so resolve through it (needs the declarations, loaded to render the picker) and force those
      // columns in too. null (no effective highlight) → no-op, so a non-register or off widget is
      // byte-identical. Only a successful schema read recomputes: when the schema was refused (or is
      // unread), an 'inherit' widget keeps the columns it was saved with instead of losing the
      // register default the highlight reads (resolvePoiForcedColumns).
      const effectivePoi = computeEffectivePoi(this.config, this.poiDeclarations);
      // A register default only matters when it is saved AND effective: the POI keys serialise only
      // on the legacy (non binding-focus) Link 3D path, and a context alone is not effective without
      // a highlight (computeEffectivePoi) - so 'inherit' counts on the highlight, or on the context
      // under an explicit highlight column.
      const highlightSource = normalizePoiSource(this.config.poiSource);
      const highlightIsExplicit = highlightSource !== undefined
        && highlightSource !== POI_SOURCE_NONE && highlightSource !== POI_SOURCE_INHERIT;
      const inheritsDefault = !!this.config.link3D && !usesBindingFocus
        && (highlightSource === POI_SOURCE_INHERIT
          || (highlightIsExplicit && normalizePoiSource(this.config.contextSource) === POI_SOURCE_INHERIT));
      resolvePoiForcedColumns({
        schemaStatus: this.poiSchemaStatus,
        effectiveColumns: effectivePoi ? [effectivePoi.highlightColumn, effectivePoi.contextColumn] : [],
        inheritsDefault,
        previousColumns: this.poiSavedColumns,
        currentColumns: columns,
        availableColumns: (this.availableColumns || []).map(c => c.name),
      }).forEach((col) => {
        if (!isHidden(col)) columns.push(col);
      });

      const sqlViewConfig = {
        // Widget config contract version (WidgetConfigV1 in aiva.api; schema at
        // GET api/DashboardWidgets/config-schema).
        schemaVersion: WIDGET_CONFIG_SCHEMA_VERSION,
        tableName: this.config.tableName,
        tableDisplayName: this.config.tableDisplayName,
        columns,
        columnTypes: Object.keys(this.config.columnTypes || {}).reduce((visible, column) => {
          if (!isHidden(column)) visible[column] = this.config.columnTypes[column];
          return visible;
        }, {}),
        columnDisplayNames: Object.keys(this.config.columnDisplayNames || {}).reduce((visible, column) => {
          if (!isHidden(column)) visible[column] = this.config.columnDisplayNames[column];
          return visible;
        }, {}),
        // Matrix types persist the generated-alias aggregation (byte-identical passthrough for
        // every other type — same object reference). New always-present keys (null when off,
        // mirroring the coloring/thresholds pattern below): stackMode drives the stacked-column
        // layout; topBottom is the baked cap (raises the server row cap 100→1000 for matrix types
        // and feeds prepareMatrixData's truncation sentinel). Both MUST live in this whitelist or
        // they silently drop on save.
        // A count/gauge never stores the group-by it hides (aggregationForType).
        aggregation: aggregationForType(sanitizeAggregationColumns(this.matrixAggregation, isHidden), this.config.messageType),
        stackMode: this.isMatrixType ? (this.config.stackMode || 'stack') : null,
        // topBottom serves two owners on disjoint widget types: the matrix cap bake (charts) and the
        // datagrid default row limit (A2). Matrix wins when present; a widget is never both.
        topBottom: (() => {
          const value = this.matrixTopBottom || this.tableTopBottomSerialized;
          return value && isHidden(value.column) ? null : value;
        })(),
        // A sort on a column that is hidden or no longer selected is dropped (sortForColumns).
        sort: this.tableSortSerialized && isHidden(this.tableSortSerialized.column)
          ? null
          : sortForColumns(this.tableSortSerialized, columns),
        axisByMetric: this.matrixAxisByMetric,
        // Dual-axis per-metric line styling (alias-keyed { color, lineType }); null for non-dual
        // widgets, {} when dual-axis but untouched. MUST live in this whitelist or it silently drops
        // on save (like axisByMetric above).
        seriesStyles: this.matrixSeriesStyles,
        ...(this.isComparisonType ? { comparison: this.comparisonSerialized } : {}),
        filters: filtersToStore((this.config.filters || []).filter(filter => filter && !isHidden(filter.column))),
        link3D: this.config.link3D,
        tagColumn,
        tagLink,
        ...(bindingFocus ? { spatialFocus: bindingFocus } : {}),
        coloring,
        // Seeded per-column status presentation survives editing without adding an authoring UI.
        ...(this.config.messageType === 'datagrid' && this.config.columnColoring != null
          ? { columnColoring: normalizeColumnColoring(Object.fromEntries(
            Object.entries(this.config.columnColoring).filter(([column]) => !isHidden(column)),
          )) }
          : {}),
        // Register POI designation (Step 3). Presence-aware tri-state — an ABSENT key is the legacy
        // signal (POI OFF), so these are spread in ONLY when the widget actually carries them: a
        // widget with no POI settings serialises byte-identically to before Step 3 (this is what
        // protects the 15 seeded widgets). 'inherit' = register default, 'none' = explicit off,
        // <column> = explicit override; poiPaintMode is 'additive' | 'dim'. The api round-trips them
        // opaquely (DashboardWidgetsController deserialize ignores unmapped keys).
        ...(!usesBindingFocus && this.config.poiSource != null && !isHidden(this.config.poiSource)
          ? { poiSource: this.config.poiSource }
          : {}),
        ...(!usesBindingFocus && this.config.contextSource != null && !isHidden(this.config.contextSource)
          ? { contextSource: this.config.contextSource }
          : {}),
        ...(!usesBindingFocus && this.config.poiPaintMode != null
          ? { poiPaintMode: this.config.poiPaintMode }
          : {}),
        // Which colour definition governs the 3D paint ('schema' | 'property'). Same absent-key
        // discipline: a widget that never chose keeps painting exactly as it does today rather than
        // being silently migrated onto a default it did not pick.
        ...(this.config.highlightColorSource != null
          ? { highlightColorSource: this.config.highlightColorSource }
          : {}),
        refreshIntervalMinutes: this.config.refreshIntervalMinutes,
        exportEnabled: this.config.exportEnabled,
        columnWidthMode: this.config.columnWidthMode,
        // Optional reader-authored context shown before the server-generated calculation details.
        // It lives in sqlViewConfig so create, edit, and duplicate retain it without a schema change.
        aboutText: this.config.aboutText || '',
        // Canonical widget-density level. Lives here, not as a top-level payload sibling like
        // messageType/fontSize, because sqlViewConfig is the only part of this payload the api
        // round-trips opaquely - the top-level shape is a typed DTO (DashboardWidget /
        // Create|UpdateDashboardWidgetCommand in aiva.api) with no WidgetSize column, so a
        // top-level widgetSize key would silently drop on save.
        widgetSize: this.config.widgetSize,
        // KPI icon (count widgets); '' when None. A client-only sqlViewConfig key the
        // api round-trips as-is (no backend change) and the A+ card render reads back.
        icon: this.config.icon || '',
        // KPI unit / footnote (count widgets); same client-only round-trip.
        unit: this.config.unit || '',
        footnote: this.config.footnote || '',
        // Gauge display variant (count): kpiVariant + normalized min/max/bands (null when not a
        // gauge). A plain count widget carries kpiVariant '' + gauge null → the widget's gauge
        // branch is inert and the KPI card renders byte-identical.
        kpiVariant: this.config.kpiVariant || '',
        gauge: this.gaugeSerialized,
        // KPI status. Manual `tone` is the fixed-colour value, kept only in Fixed mode. Dynamic
        // `thresholds` (count Auto mode, >=1 boundary) is an absolute-band object recomputed
        // client-side on every fetch; null when Fixed mode / no boundary / not a count. Mirrors
        // the `coloring` pattern (always-present key, null when off) — an ABSENT thresholds
        // renders neutral, so we never emit a degenerate {rules:[]} (which would paint good).
        tone: this.kpiSerializedTone,
        thresholds: this.kpiThresholds,
        // KPI coloring source + schema selection (count Schema mode). Additive, always-present
        // keys mirroring the `coloring`/`thresholds` null-when-off pattern: null in Auto/Fixed so a
        // legacy/absent widget keeps the shipped path byte-identically; the consumer only reads them
        // when coloringSource === 'schema'. schemaSelection is { kind:'builtin', schemaId, valueUnit }
        // or { kind:'inline', definition, formatVersion } (§4.1/§4.3).
        coloringSource: this.kpiSerializedColoringSource,
        schemaSelection: this.kpiSchemaSelection,
        // Semantic source contract (#7/#9): both are client-only sqlViewConfig keys the api
        // round-trips opaquely (no top-level DTO column), consumed by the cross-widget
        // selection/coloring rules (#10). sourceKey recomputes from the CURRENT table + filters
        // so an edited query scope re-keys (its whole purpose is distinguishing differently
        // filtered charts on one table); when it cannot be derived (no active facility) a
        // previously persisted key is kept rather than dropped, and an absent key falls through
        // to runtime resolveSourceKey on read. createdFromWidgetId is immutable provenance (the
        // grid a chart was seeded from), carried through verbatim; undefined omits both keys.
        sourceKey: buildSourceKey({
          facilityId: this.facilityId,
          tableName: this.config.tableName,
          filters: this.config.filters,
        }) || this.config.sourceKey || undefined,
        createdFromWidgetId: this.config.createdFromWidgetId || undefined,
      };

      if (sqlViewConfig.coloringSource) {
        log('EditSqlViewWidget', 'serialize schema selection', JSON.stringify(sqlViewConfig.schemaSelection));
      }

      const payload = {
        title: this.config.title || this.config.tableDisplayName || this.config.tableName,
        subTitle: this.config.subTitle || '',
        messageType: this.config.messageType,
        // Legacy numeric mirror, derived from config.widgetSize so it can never disagree with
        // the canonical value now stored in sqlViewConfig.widgetSize above - config.fontSize
        // itself is stale the moment the widget-size toggle is used (it no longer writes to it).
        fontSize: LEGACY_FONT_SIZE_BY_WIDGET_SIZE[this.config.widgetSize],
        legend: this.config.legend,
        aiGenerated: false,
        // Keys this wizard does not own (loaded with the widget) are carried over unchanged, except
        // a listDrill that no longer fits the source being saved (preservedEntriesForSource).
        sqlViewConfig: JSON.stringify(withPreservedEntries(sqlViewConfig, preservedEntriesForSource(
          this.preservedConfig,
          {
            loadedTableName: this.preservedConfigTable,
            tableName: this.config.tableName,
            // Hidden columns are still source columns, so they count too.
            columnNames: [...this.availableColumns.map(column => column.name), ...this.hiddenColumnNames],
          },
        ))),
      };

      if (this.isEditing && this.editingWidgetId) {
        payload.id = this.editingWidgetId;
      }

      return payload;
    },

    fetchViews() {
      this.viewsLoading = true;
      apiService.tables()
        .then(res => {
          this.availableViews = res.data || [];
          const selectedView = this.availableViews.find(
            view => String(view?.name).toLowerCase() === String(this.config.tableName || '').toLowerCase(),
          );
          if (selectedView && Object.prototype.hasOwnProperty.call(selectedView, 'tagLink')) {
            this.config.tagLink = selectedView.tagLink;
          }
          if (selectedView && Object.prototype.hasOwnProperty.call(selectedView, 'spatial')) {
            this.config.spatial = selectedView.spatial;
          }
        })
        .catch(err => {
          error('EditSqlViewWidget', 'fetchViews error', err);
        })
        .finally(() => {
          this.viewsLoading = false;
        });
    },

    /** Fetch the current view's register link declarations (Step 4) via the same two-GET bridge the
     *  grid uses: the view list carries registerId (Step 3a) → GET the register schema. Cleared when
     *  there is no register id; a failed fetch also leaves declarations empty, so the picker hides
     *  and computeEffectivePoi resolves OFF (never throws into the wizard). poiSchemaStatus records
     *  the outcome: only OK lets a save recompute the register-default POI columns. */
    async fetchPoiDeclarations() {
      const registerId = this.poiRegisterId;
      if (!registerId) {
        this.poiSchemaStatus = POI_SCHEMA_STATUS.IDLE;
        this.poiDeclarations = [];
        return;
      }
      this.poiSchemaStatus = POI_SCHEMA_STATUS.PENDING;
      try {
        const schema = await registerService.getRegisterSchema(registerId);
        if (this.poiRegisterId !== registerId) return;
        this.poiDeclarations = Array.isArray(schema?.columns) ? schema.columns : [];
        this.poiSchemaStatus = POI_SCHEMA_STATUS.OK;
      } catch (err) {
        if (this.poiRegisterId !== registerId) return;
        this.poiSchemaStatus = poiSchemaStatusFromError(err);
        if (this.poiSchemaStatus === POI_SCHEMA_STATUS.DENIED) {
          log('EditSqlViewWidget', 'register schema not readable by this user; POI picker hidden, saved POI columns kept', registerId);
        } else {
          error('EditSqlViewWidget', 'fetchPoiDeclarations error', err);
        }
        this.poiDeclarations = [];
      }
    },

    fetchColumns(tableName) {
      this.columnsLoading = true;
      this.columnsConfigRequired = false;
      return apiService.tableColumns(tableName)
        .then(res => {
          const columns = Array.isArray(res.data) ? res.data : [];
          this.hiddenColumnNames = columns
            .filter(isHiddenColumnMetadata)
            .map(column => column.name);
          this.availableColumns = columns.filter(column => !isHiddenColumnMetadata(column));
        })
        .catch(err => {
          this.columnsConfigRequired = isRegisterConfigurationRequiredError(err);
          if (this.columnsConfigRequired) {
            log('EditSqlViewWidget', 'source register needs an assignment mode', tableName);
          } else {
            error('EditSqlViewWidget', 'fetchColumns error', err);
          }
          this.availableColumns = [];
          this.hiddenColumnNames = [];
        })
        .finally(() => {
          this.columnsLoading = false;
        });
    },

    /** Picker gate: `/tables` flags a Group register that cannot be read until configured. */
    isSourceNotConfigured(item) {
      return isConfigurationRequiredSource(item);
    },

    onViewSelected(viewName) {
      if (!viewName) return;

      // Store the display name from the selected view
      const selectedView = this.availableViews.find(v => v.name === viewName);
      this.config.tableDisplayName = selectedView?.displayName || null;
      this.config.tagLink = selectedView && Object.prototype.hasOwnProperty.call(selectedView, 'tagLink')
        ? selectedView.tagLink
        : undefined;
      this.config.spatial = selectedView && Object.prototype.hasOwnProperty.call(selectedView, 'spatial')
        ? selectedView.spatial
        : undefined;

      // Reset columns and config when view changes
      this.config.columns = [];
      this.config.columnTypes = {};
      this.config.aggregation = { groupBy: [], metrics: [] };
      this.config.filters = [];
      this.config.link3D = false;
      this.config.tagColumn = null;
      this.config.coloring = { column: null, valueColors: {} };
      this.coloringValues = [];
      this.columnSamples = {};
      // POI is keyed to the OLD register's declarations; drop it so a leftover poiSource can't ride
      // onto a different (or non-register) view — matches the coloring reset just above. The saved
      // columns belong to the old view too, so none of them may be carried into this one.
      this.resetPoiConfig();
      this.poiSavedColumns = null;

      this.fetchColumns(viewName).then(() => {
        // Select all columns by default
        this.onColumnsChanged(this.availableColumns.map(c => c.name));
      });
    },

    /** Chooser v-model handler: `names` is the selected column-name array. Keeps the
     *  persisted columnTypes / columnDisplayNames maps in sync with the selection. */
    onColumnsChanged(names) {
      const selected = this.availableColumns.filter(c => names.includes(c.name));
      this.config.columns = selected.map(c => c.name);
      this.config.columnTypes = {};
      this.config.columnDisplayNames = {};
      selected.forEach(c => {
        this.config.columnTypes[c.name] = c.dataType;
        if (c.displayName) {
          this.config.columnDisplayNames[c.name] = c.displayName;
        }
      });
    },

    /** Harvest one sample value per column from the preview's fetch, so the chooser can
     *  show recognisable data under each column name. Accumulates per source (a value,
     *  once seen, is kept); only keys matching real columns are taken, which naturally
     *  ignores chart-shaped preview payloads. */
    onPreviewData(data) {
      const rows = data && Array.isArray(data.content) ? data.content : [];
      if (!rows.length || !this.availableColumns.length) return;

      const known = new Set(this.availableColumns.map(c => c.name));
      const merged = { ...this.columnSamples };
      rows.forEach(row => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return;
        Object.keys(row).forEach(key => {
          if (!known.has(key) || merged[key] !== undefined) return;
          const value = row[key];
          if (value === null || value === undefined || value === '') return;
          merged[key] = value;
        });
      });
      this.columnSamples = merged;
    },

    /** Turning off Link 3D discards the colour-coding AND the POI config (both only apply in 3D). */
    onLink3DChange(enabled) {
      if (!enabled) {
        this.config.coloring = { column: null, valueColors: {} };
        this.coloringValues = [];
        this.resetPoiConfig();
      }
    },

    /** Clear the register-POI designation (Step 4). Set to null — buildWidgetPayload's `!= null`
     *  guard treats null as absent, so a view switch or a Link-3D toggle-off leaves the widget
     *  byte-identical (no poiSource/contextSource/poiPaintMode serialised), exactly as `coloring`
     *  resets. $set keeps an initially-absent key reactive so the picker re-reads Off. */
    resetPoiConfig() {
      this.$set(this.config, 'poiSource', null);
      this.$set(this.config, 'contextSource', null);
      this.$set(this.config, 'poiPaintMode', null);
      // Cleared with the rest: the colour-source choice is only meaningful alongside a highlight, and
      // leaving it behind would re-serialise a key the widget no longer uses.
      this.$set(this.config, 'highlightColorSource', null);
    },

    /** Picking (or clearing) a colouring column resets the value→colour map and reloads
     *  the column's distinct values so a swatch can be assigned per value. */
    onColoringColumnChange(column) {
      this.config.coloring = { column: column || null, valueColors: {} };
      this.coloringValues = [];
      if (column) this.fetchColoringValues();
    },

    /** Loads the distinct values of the colouring column (same endpoint the widget filter
     *  builder uses) and seeds a default palette colour for each so the map is usable
     *  immediately; the user can override any swatch. */
    fetchColoringValues() {
      const column = this.config.coloring.column;
      if (!this.config.tableName || !column) return;
      this.coloringValuesLoading = true;
      apiService.columnDistinctValues(this.config.tableName, column, { take: 200 })
        .then(res => {
          const result = res.data;
          this.coloringValues = Array.isArray(result) ? result : (result?.data || []);
          this.seedColoringDefaults();
        })
        .catch(err => {
          error('EditSqlViewWidget', 'fetchColoringValues error', err);
          this.coloringValues = [];
        })
        .finally(() => {
          this.coloringValuesLoading = false;
        });
    },

    /** Assigns a palette colour to any value that has no colour yet (preserves existing
     *  assignments, e.g. when editing a saved widget). */
    seedColoringDefaults() {
      const map = this.config.coloring.valueColors || {};
      let paletteIdx = Object.keys(map).length;
      this.coloringValues.forEach(val => {
        const key = String(val);
        if (!map[key]) {
          this.$set(map, key, COLORING_PALETTE[paletteIdx % COLORING_PALETTE.length]);
          paletteIdx++;
        }
      });
      this.config.coloring.valueColors = map;
    },

    /** Rebuild the KPI status editor fields from a saved sqlViewConfig. thresholds is the source
     *  of truth when present (Auto mode: direction from the field, boundaries from the rules by
     *  tone); otherwise a saved manual tone means Fixed mode; neither means the Auto default.
     *  Auto mode owns the colour, so any stale manual tone is cleared. */
    restoreKpiStatusState(cfg) {
      const thresholds = cfg && cfg.thresholds;
      const rules = thresholds && Array.isArray(thresholds.rules) ? thresholds.rules : null;
      if (rules && rules.length) {
        this.config.statusMode = STATUS_MODE.AUTO;
        this.config.direction = thresholds.direction === KPI_DIRECTION.LOWER_IS_WORSE ? KPI_DIRECTION.LOWER_IS_WORSE : KPI_DIRECTION.HIGHER_IS_WORSE;
        const caution = rules.find(r => r && r.tone === TONE.CAUTION);
        const critical = rules.find(r => r && r.tone === TONE.CRITICAL);
        this.config.cautionValue = caution && caution.value != null ? caution.value : null;
        this.config.criticalValue = critical && critical.value != null ? critical.value : null;
        this.config.tone = '';
      } else if (this.config.tone) {
        this.config.statusMode = STATUS_MODE.FIXED;
      } else {
        this.config.statusMode = STATUS_MODE.AUTO;
      }
    },

    /** A boundary is "set" when it is a real number. v-model.number yields '' when the field is
     *  cleared and null in defaults; 0 is a legitimate boundary (e.g. critical <= 0), so it must
     *  count as set. */
    kpiHasValue(v) {
      return v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
    },

    /** Coerce a boundary field to a number, or null when blank/invalid. */
    kpiNumber(v) {
      return this.kpiHasValue(v) ? Number(v) : null;
    },

    /** Schema dropdown change: seed the value-unit from the picked schema's own unit (built-in or
     *  library) so a unit-bearing schema converts onto its bands. The legacy inline sentinel is not
     *  selectable for creation, so there is no inline branch. */
    onSchemaSelected(id) {
      const schema = getBuiltInSchema(id) || this.kpiSchemaLibrary.find(s => s && s.id === id);
      this.config.schemaValueUnit = schema && schema.unit ? schema.unit : '';
    },

    /** Drop the current schema pick when it no longer applies to the widget's display type (called on
     *  a display-type switch). A resolved built-in/library schema whose appliesTo is outside the new
     *  type's applicableSchemaKinds (empty for matrix types) is cleared, and both coloring modes fall
     *  back to their defaults so save cannot dead-end on a stale, invalid selection. A legacy inline or
     *  an unresolved pick (library still loading) is left untouched - kpiSelectedSchema is null, so
     *  there is nothing to test; built-ins resolve synchronously, covering the common inspection scales.
     *  Returns true when it actually cleared a schema (so the caller can surface the inline notice). */
    resetSchemaIfIncompatible() {
      const schema = this.kpiSelectedSchema;
      if (!schema) return false;
      if (!this.applicableSchemaKinds.includes(schema.appliesTo)) {
        this.config.schemaId = '';
        this.config.schemaValueUnit = '';
        this.config.chartColoringMode = CHART_COLORING_MODE.STATIC;
        if (this.config.statusMode === STATUS_MODE.SCHEMA) this.config.statusMode = STATUS_MODE.AUTO;
        return true;
      }
      return false;
    },

    /** Two starter bands for a fresh gauge: good below the (default range) midpoint, critical above.
     *  The last band is unbounded above = "up to max" on the dial. */
    defaultGaugeBands() {
      return [
        { to: 50, tone: TONE.GOOD, color: null, label: 'Good' },
        { to: null, tone: TONE.CRITICAL, color: null, label: 'Critical' },
      ];
    },

    /** Rebuild the schema selection working state from a saved schemaSelection (edit mode). A legacy
     *  inline is stashed and re-emitted verbatim (never rebuilt — a rebuild would drift); a built-in
     *  or library reference maps to its id + valueUnit so the dropdown reselects it. */
    restoreSchemaSelection(selection) {
      if (!selection || typeof selection !== 'object') {
        this.config.schemaId = '';
        return;
      }
      if (selection.kind === SCHEMA_KIND.INLINE) {
        // Legacy inline (no longer creatable): stash the ORIGINAL so it re-serializes byte-identically.
        this.legacyInlineSelection = selection;
        this.config.schemaId = CUSTOM_SCHEMA_ID;
        log('EditSqlViewWidget', 'restore legacy inline schema (read-only)', selection.definition && selection.definition.id);
      } else if (selection.kind === SCHEMA_KIND.BUILTIN || selection.kind === SCHEMA_KIND.REFERENCE) {
        this.config.schemaId = selection.schemaId || '';
        this.config.schemaValueUnit = selection.valueUnit || '';
        log('EditSqlViewWidget', 'restore schema selection', selection.kind, selection.schemaId, selection.valueUnit);
      } else {
        this.config.schemaId = '';
      }
    },

    /** A hex for a schema entry/range swatch in the read-only preview: the tone's representative
     *  colour, a raw color pair's light hex, or the neutral grey. */
    schemaPaintSwatch(item) {
      if (item.tone) {
        const opt = this.kpiToneOptions.find(t => t.value === item.tone);
        return opt ? opt.color : '#9E9E9E';
      }
      if (item.color && item.color.light) return item.color.light;
      return '#9E9E9E';
    },

    /** A short "under 0.05" / "0.05 to 0.1" / "0.8 and up" label for a number range in the preview
     *  strip (a fallback only — static ranges carry their own label). */
    schemaRangeLabel(range, unit) {
      const u = unit ? ` ${unit}` : '';
      if (range.from == null) return `under ${range.to}${u}`;
      if (range.to == null) return `${range.from}${u} and up`;
      return `${range.from} to ${range.to}${u}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.sql-view-wizard {
  font-family: 'Poppins', sans-serif;
}

// AVA-602 data-source picker row. The disabled-row hue comes from Vuetify; the reason line
// and chip keep a readable text token so the explanation is not greyed to illegibility.
.dsrc-item {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
  width: 100%;
  min-width: 0;
}

.dsrc-item__text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

.dsrc-item__hint {
  font-size: var(--ds-font-caption);
  line-height: 1.3;
  color: var(--v-text-base);
  opacity: 0.7;
}

.dsrc-item__chip {
  flex: 0 0 auto;
  color: var(--v-text-base) !important;
  opacity: 0.75;
}

.dsrc-config-required {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-xs);
}

.dsrc-config-required__title {
  font-size: var(--ds-font-body-sm);
  font-weight: 600;
  color: var(--v-text-base);
}

.dsrc-config-required__text {
  font-size: var(--ds-font-caption);
  line-height: 1.4;
  color: var(--v-text-base);
  opacity: 0.7;
}

.sql-view-wizard-steps {
  .v-btn {
    text-transform: none !important;
    letter-spacing: normal !important;
    font-size: 12px;
  }
}

// Config-pane layout MUST live in a class, not an inline display:flex — Vue reuses
// these unkeyed step divs across step switches and v-show then restores the display
// it memorized from the reused element, silently dropping an inline `flex` (which
// also disables `gap`).
.sql-view-config-pane {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: auto;
}

.sql-view-config-pane--fill {
  height: 100%;
}

.sql-view-config-pane--gap-lg {
  gap: 24px;
}

.sql-view-config-pane--gap-md {
  gap: 16px;
}

// Behavior-step wrapper lets the parent-owned comparison selector join the existing step without
// changing WizardStepBehavior's public contract. The child yields scrolling to this single pane.
.sql-view-behavior-pane {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
  gap: 24px;

  ::v-deep .wizard-step-behavior {
    flex: 0 0 auto;
    height: auto;
    overflow: visible;
  }
}

// Step-2 always-open section blocks (replaced the expansion-panel accordion, which
// snapped siblings shut and overflowed its container by 3px → stray h-scrollbar)
.viz-section {
  flex: 0 0 auto;
  border: 1px solid #e2e6e4;
  border-radius: 8px;
  padding: 14px 16px 16px;
}

.viz-section__head {
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  .ds-field-label {
    margin-bottom: 0;
  }
}

.viz-section__action {
  margin-left: auto;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

// Field caption used by the Widget Details group (title/subtitle) at the top of the Build step.
.viz-sub-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
  opacity: 0.75;
}

.viz-sub-hint {
  display: block;
  margin-top: 6px;
  font-size: 11.5px;
  opacity: 0.6;
}

.viz-narrow-select {
  max-width: 340px;
}

.viz-metric-hint {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 10px;
  padding: 8px 12px;
  font-size: 12.5px;
  border-radius: 6px;
  background: rgba(3, 98, 76, 0.06);
  border: 1px solid rgba(3, 98, 76, 0.14);
}

// Destructive type-switch notice on the Build-visualization step: a light informational row
// (icon + message + Dismiss). Mirrors the metric-hint treatment (a subtle full border, no accent
// bar) so it reads as guidance, not an error.
.viz-type-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 8px 12px;
  font-size: 12.5px;
  border-radius: 6px;
  background: rgba(3, 98, 76, 0.06);
  border: 1px solid rgba(3, 98, 76, 0.14);
}

.viz-type-notice__text {
  flex: 1 1 auto;
}

.viz-type-notice__dismiss {
  flex: 0 0 auto;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.theme--dark {
  .viz-section {
    border-color: rgba(255, 255, 255, 0.12);
  }

  .viz-metric-hint,
  .viz-type-notice {
    background: rgba(44, 194, 149, 0.08);
    border-color: rgba(44, 194, 149, 0.2);
  }
}
</style>
